/**
 * AI Tracker Content 컴포넌트
 *
 * AITracker 페이지의 컨텐츠 부분을 모달 등에서 재사용하기 위한 컴포넌트
 * 학생 검색 없이 studentId를 직접 받아서 데이터를 표시
 */

import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchMathConcepts } from '../utils/mathConceptsUtils';
// 403 문구는 세 자리(이 파일의 genetics 둘 · pages/AITracker)가 한 자리에서 만든다 —
// 이 경로들은 공용 apiUtils 를 지나지 않는 raw fetch 라 기존 공통 자리가 없었다
import forbiddenText from '../utils/forbiddenText';
import { ToggleLeft, ToggleRight, ChevronDown, ChevronRight, Check, ListChecks, ArrowUpDown, ArrowUp, ArrowDown, Calendar, RotateCcw, Camera, Play, Pause, X, TrendingUp } from 'lucide-react';
import {
  timelineKey, fingerprintOf, kstDateOf, extractFrame, frameDelta,
  loadEnvelope, saveEnvelope, TIMELINE_VERSION, computeFrozenBounds,
  timelineFingerprintOf, framesForDates,
} from '../utils/aitrackerTimelineCache';
import { collectDefaultCids, shouldResetRanges } from '../utils/aitrackerFilters';
import { projectPoint, relativeRadius } from '../utils/aitrackerProjection';
import {
  ScatterChart, Scatter, XAxis, YAxis,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import MathceptDetailModal from './common/MathceptDetailModal';
import ConceptTrendModal from './ConceptTrendModal';
import katex from 'katex';
import 'katex/dist/katex.min.css';

// concept_type에 따른 색상 매핑 (무지개색 50% 투명도)
const getConceptTypeColor = (type) => {
  const colors = {
    'DEF': 'rgba(239, 68, 68, 0.5)',
    'AXM': 'rgba(249, 115, 22, 0.5)',
    'IDN': 'rgba(245, 158, 11, 0.5)',
    'THM': 'rgba(34, 197, 94, 0.5)',
    'MTH': 'rgba(6, 182, 212, 0.5)',
    'PRP': 'rgba(59, 130, 246, 0.5)',
    'FOR': 'rgba(139, 92, 246, 0.5)',
    'NTN': 'rgba(236, 72, 153, 0.5)'
  };
  return colors[type] || 'rgba(156, 163, 175, 0.5)';
};

// math_type에 따른 색상 매핑
const getMathTypeColor = (type) => {
  const colors = {
    'AL': 'rgba(99, 102, 241, 0.5)',
    'AN': 'rgba(244, 63, 94, 0.5)',
    'GE': 'rgba(16, 185, 129, 0.5)',
    'PS': 'rgba(168, 85, 247, 0.5)',
  };
  return colors[type] || 'rgba(107, 114, 128, 0.5)';
};

// rgba(r,g,b,a) → { color:'rgb(r,g,b)', opacity:a }. gradient stop은 stop-opacity로 알파를 보존해
// 구 브라우저에서도 반투명(0.5)이 정확히 유지되게 한다(fill=rgba와 픽셀 동일).
const splitRgba = (c) => {
  const m = /rgba?\(([^)]+)\)/i.exec(c);
  if (!m) return { color: c, opacity: 1 };
  const p = m[1].split(',').map((s) => s.trim());
  return { color: `rgb(${p[0]}, ${p[1]}, ${p[2]})`, opacity: p.length >= 4 ? parseFloat(p[3]) : 1 };
};
// 두 반원 색쌍 → 문서 내 유일 gradient id. 비영숫자를 '-'로(경계 보존 → 색값 충돌 방지).
const dotGradId = (left, right) => (`dg-${left}--${right}`).replace(/[^a-z0-9]+/gi, '-');

// 일별 재생 타이밍: 틱보다 짧은 활공(≈0.7×)으로 프레임마다 정착(dwell) 확보 → 궤적 가독성.
const PLAYBACK_TICK_MS = 700;
const PLAYBACK_GLIDE_MS = Math.round(PLAYBACK_TICK_MS * 0.7);

// 좌표평면 dot 반지름 범위(px): st(안정도)가 클수록 큰 dot
const DOT_MIN_RADIUS = 4;
const DOT_MAX_RADIUS = 14;

// concept_type 한글 이름 매핑
const getConceptTypeName = (type) => {
  const names = {
    'DEF': '정의',
    'AXM': '공리',
    'IDN': '정리',
    'THM': '이론',
    'MTH': '방법론',
    'PRP': '성질',
    'FOR': '공식',
    'NTN': '표기법'
  };
  return names[type] || type;
};

// 오답률에 따른 텍스트 색상 반환
const getErrorRateColor = (errorRate) => {
  const rate = parseFloat(errorRate);
  if (rate >= 90) return 'text-red-900';
  if (rate >= 80) return 'text-red-800';
  if (rate >= 70) return 'text-red-800';
  if (rate >= 60) return 'text-red-700';
  if (rate >= 50) return 'text-red-700';
  if (rate >= 40) return 'text-red-600';
  if (rate >= 30) return 'text-red-500';
  if (rate >= 20) return 'text-red-400';
  if (rate >= 10) return 'text-red-300';
  return 'text-red-300';
};

// 오답률에 따른 배경 색상 반환
const getErrorRateBgColor = (errorRate) => {
  const rate = parseFloat(errorRate);
  if (rate >= 90) return 'bg-red-900';
  if (rate >= 80) return 'bg-red-800';
  if (rate >= 70) return 'bg-red-800';
  if (rate >= 60) return 'bg-red-700';
  if (rate >= 50) return 'bg-red-700';
  if (rate >= 40) return 'bg-red-600';
  if (rate >= 30) return 'bg-red-500';
  if (rate >= 20) return 'bg-red-400';
  if (rate >= 10) return 'bg-red-300';
  return 'bg-red-300';
};

// 스냅샷 무지개색 (빨주노초파남보, 7가지 - 8회차부터 순환)
const RAINBOW_COLORS = [
  'bg-red-200 text-red-800',
  'bg-orange-200 text-orange-800',
  'bg-yellow-200 text-yellow-800',
  'bg-green-200 text-green-800',
  'bg-blue-200 text-blue-800',
  'bg-indigo-200 text-indigo-800',
  'bg-purple-200 text-purple-800',
];

// stability 값(0~1)에 따라 연한 초록 ~ 진한 초록 색상 반환
const getStabilityColor = (stability) => {
  if (stability === null || stability === undefined) return '#d1fae5'; // 연한 초록
  const normalized = Math.max(0, Math.min(1, stability)); // 0~1 clamp
  // 연한 초록(#d1fae5) → 진한 초록(#047857)
  const r = Math.round(209 - normalized * (209 - 4));
  const g = Math.round(250 - normalized * (250 - 120));
  const b = Math.round(229 - normalized * (229 - 87));
  return `rgb(${r}, ${g}, ${b})`;
};

// ScatterChart 커스텀 툴팁
const ScatterTooltip = ({ active, payload }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="rounded-lg border bg-white p-2 shadow-lg">
        <div className="font-medium" dangerouslySetInnerHTML={{ __html: data.name }} />
        <div className="mt-1 text-sm text-gray-600">
          <div>r: {data.x?.toFixed(2)}</div>
          <div>h: {data.y?.toFixed(2)}</div>
          <div>stability: {data.z?.toFixed(2)}</div>
        </div>
      </div>
    );
  }
  return null;
};

// 테이블 셀 유틸 함수
const formatVal = (v) => v != null ? (Math.trunc(v * 100) / 100).toFixed(2) : '';
const calcWeight = (v) => Math.round(400 + Math.abs(v ?? 0) * 500);
const valColor = (v) => v > 0 ? 'text-green-600' : v < 0 ? 'text-red-500' : 'text-gray-600';

// 수학개념 테이블 컬럼 표시 라벨 (정렬 키 r/h/st는 유지, 화면 표기만 매핑)
const COLUMN_LABELS = { r: '직접', h: '연관', st: '안정도' };

// 공통 개념 테이블 행 컴포넌트 (Chart Side, R, H, St 테이블 공용)
const ConceptTableRow = React.memo(({ row, onClick, selectedCids = [] }) => {
  const sel = selectedCids.includes(row.cid);
  // 행 구분선은 각 td로 이동(border-separate 모드에선 tr의 border가 무시됨), 선택 시 녹색 음영
  const tdCls = `border-b border-gray-100 ${sel ? 'bg-primary/15' : ''}`;
  return (
  <tr
    data-cid={row.cid}
    className={`cursor-pointer transition-colors ${sel ? '' : 'hover:bg-gray-50'}`}
    onClick={() => onClick(row.cid, row.name)}
  >
    <td className={`px-2 py-1 max-w-0 ${tdCls} ${sel ? 'rounded-l-3xl' : ''}`}>
      <div className="flex items-center gap-1 text-xs whitespace-nowrap">
        {row.order != null && (
          <span
            className="inline-flex items-center justify-center shrink-0 font-semibold text-gray-700"
            style={{ backgroundColor: 'rgba(156, 163, 175, 0.3)', borderRadius: '6px', width: '24px', height: '16px', minWidth: '24px', fontSize: '9px' }}
          >
            {row.order}
          </span>
        )}
        {row.mathType && (
          <span
            className="inline-flex items-center justify-center shrink-0 rounded-md px-1 text-[9px] font-semibold text-gray-700"
            style={{ backgroundColor: getMathTypeColor(row.mathType), minWidth: '24px', height: '16px' }}
          >
            {row.mathType}
          </span>
        )}
        {row.conceptType && (
          <span
            className="inline-flex items-center justify-center shrink-0 rounded-md px-1 text-[8px] font-semibold text-gray-800"
            style={{ backgroundColor: getConceptTypeColor(row.conceptType), minWidth: '24px', height: '16px' }}
          >
            {row.conceptType}
          </span>
        )}
        <span className="truncate min-w-0" dangerouslySetInnerHTML={{ __html: row.name }} />
        {row.tags && row.tags.length > 0 && (
          <span className="inline-flex items-center gap-1 ml-auto shrink-0">
            {row.tags.slice(0, 3).map((tag, idx) => (
              <span key={idx} className="inline-flex items-center rounded px-1 text-[9px] font-medium" style={{ backgroundColor: '#e0f2fe', color: '#0369a1', height: '14px', lineHeight: '14px' }}>{tag}</span>
            ))}
            {row.tags.length > 3 && <span className="text-[9px] text-gray-400">+{row.tags.length - 3}</span>}
          </span>
        )}
      </div>
    </td>
    <td className={`px-2 py-1 text-right tabular-nums ${tdCls} ${valColor(row.x)}`} style={{ fontWeight: calcWeight(row.x) }}>{formatVal(row.x)}</td>
    <td className={`px-2 py-1 text-right tabular-nums ${tdCls} ${valColor(row.y)}`} style={{ fontWeight: calcWeight(row.y) }}>{formatVal(row.y)}</td>
    <td className={`px-2 py-1 text-right tabular-nums text-gray-600 ${tdCls} ${sel ? 'rounded-r-3xl' : ''}`} style={{ fontWeight: calcWeight(row.z) }}>{formatVal(row.z)}</td>
  </tr>
  );
});

// 수학개념 테이블 타이틀의 태그 분류 필터 (개념의 첫 번째 태그 기준, 단일 선택)
const TagFilterSelect = ({ tags, value, onChange }) => {
  if (!tags || tags.length === 0) return null;
  return (
    <select
      value={value || ''}
      onChange={(e) => onChange(e.target.value || null)}
      onClick={(e) => e.stopPropagation()}
      title="태그 분류 필터 (첫 번째 태그 기준)"
      className={`ml-1 max-w-[110px] rounded border px-1 py-0.5 text-[10px] font-normal bg-sky-100 text-sky-700 ${value ? 'border-sky-400' : 'border-sky-200'}`}
    >
      <option value="">전체 태그</option>
      {tags.map((tag) => (
        <option key={tag} value={tag}>{tag}</option>
      ))}
    </select>
  );
};

// 가로 막대그래프 컴포넌트
const HorizontalBar = ({ correct, incorrect, maxTotal }) => {
  const total = correct + incorrect;
  if (total === 0) {
    return <div className="flex items-center justify-end text-xs text-gray-400">-</div>;
  }

  const correctPercent = (correct / total) * 100;
  const incorrectPercent = (incorrect / total) * 100;
  const barWidthPercent = (total / maxTotal) * 100;
  const errorRate = ((incorrect / total) * 100).toFixed(1);

  return (
    <div className="flex items-center gap-1">
      <div className="flex-1">
        <div
          className="flex h-4 overflow-hidden rounded"
          style={{ width: `${barWidthPercent}%`, minWidth: '8px' }}
        >
          <div
            className="h-full bg-primary"
            style={{ width: `${correctPercent}%` }}
            title={`정답: ${correct}`}
          />
          <div
            className={`h-full ${getErrorRateBgColor(errorRate)}`}
            style={{ width: `${incorrectPercent}%` }}
            title={`오답: ${incorrect}`}
          />
        </div>
      </div>
      <div className="flex w-[42px] shrink-0 flex-col items-center">
        <span className="text-[10px]">
          <span className="font-bold text-red-500">{incorrect}</span>
          <span className="text-gray-400">/</span>
          <span className="font-bold text-gray-900">{total}</span>
        </span>
        <span className={`text-[9px] ${getErrorRateColor(errorRate)}`}>
          (<span className="font-bold">{errorRate}%</span>)
        </span>
      </div>
    </div>
  );
};

// 세로 막대그래프 컴포넌트
const VERTICAL_BAR_TOTAL_HEIGHT = 50;
const VERTICAL_BAR_MAX_HEIGHT = 32;
const VERTICAL_BAR_MIN_HEIGHT = 6;

const VerticalBar = ({ correct, incorrect, maxDifficultyTotal }) => {
  const total = correct + incorrect;

  if (total === 0) {
    return (
      <div
        className="flex w-full items-center justify-center bg-gray-100 text-[9px] text-gray-400"
        style={{ height: `${VERTICAL_BAR_TOTAL_HEIGHT}px` }}
      >
        -
      </div>
    );
  }

  const cellHeight = Math.max(VERTICAL_BAR_MIN_HEIGHT, (total / maxDifficultyTotal) * VERTICAL_BAR_MAX_HEIGHT);
  const correctHeight = (correct / total) * cellHeight;
  const incorrectHeight = (incorrect / total) * cellHeight;
  const errorRate = ((incorrect / total) * 100).toFixed(1);

  return (
    <div className="flex w-full flex-col" style={{ height: `${VERTICAL_BAR_TOTAL_HEIGHT}px` }}>
      <div className="flex w-full flex-col items-center pt-1 leading-none">
        <span className="text-[9px] font-semibold">
          <span className="text-primary">{correct}</span>
          <span className="text-gray-400">/</span>
          <span className="text-red-500">{incorrect}</span>
        </span>
        <span className={`text-[7px] ${getErrorRateColor(errorRate)}`}>
          (<span className="font-bold">{errorRate}%</span>)
        </span>
      </div>
      <div className="flex-1" />
      <div className="relative w-full" style={{ height: `${cellHeight}px` }}>
        <div
          className={`absolute bottom-0 left-0 w-full ${getErrorRateBgColor(errorRate)}`}
          style={{ height: `${incorrectHeight}px` }}
          title={`오답: ${incorrect}`}
        />
        <div
          className="absolute left-0 w-full bg-primary"
          style={{ bottom: `${incorrectHeight}px`, height: `${correctHeight}px` }}
          title={`정답: ${correct}`}
        />
      </div>
    </div>
  );
};

// 날짜 범위 슬라이더 컴포넌트
// compact(학습 화면만 켠다): 날짜는 입력칸 한 곳에만 보인다 — 필터가 없으면 전체 기간, 끄는 동안엔 끄는 날짜.
//   막대 위 전체 기간 줄·손잡이 밑 날짜를 빼고 일수는 슬라이더 오른쪽 끝으로, 슬라이더 칸은 손잡이 높이(20px)만 남긴다(실측 104.4px → 69.4px).
//   일수를 입력칸 줄에 두지 않는 것은 그 줄이 학습 화면 폭(상자 300~424px)에서 이미 넘쳐 끝이 가려지기 때문이다.
//   전체 기간은 막대에 올린 설명(title)으로 남긴다.
export const DateRangeSlider = ({ minDate, maxDate, startDate, endDate, onStartDateChange, onEndDateChange, onDragEnd, onReset, isLoading, compact = false }) => {
  const [draggingStart, setDraggingStart] = useState(null);
  const [draggingEnd, setDraggingEnd] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const sliderRef = useRef(null);

  const dateToNumber = (dateStr) => {
    if (!dateStr) return null;
    return Math.floor(new Date(dateStr).getTime() / (1000 * 60 * 60 * 24));
  };

  const numberToDate = (num) => {
    const date = new Date(num * 1000 * 60 * 60 * 24);
    return date.toISOString().split('T')[0];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
  };

  const minNum = dateToNumber(minDate);
  const maxNum = dateToNumber(maxDate);

  const displayStartDate = isDragging && draggingStart !== null ? draggingStart : startDate;
  const displayEndDate = isDragging && draggingEnd !== null ? draggingEnd : endDate;

  const startNum = dateToNumber(displayStartDate) || minNum;
  const endNum = dateToNumber(displayEndDate) || maxNum;
  const totalDays = maxNum - minNum;

  const startPercent = totalDays > 0 ? ((startNum - minNum) / totalDays) * 100 : 0;
  const endPercent = totalDays > 0 ? ((endNum - minNum) / totalDays) * 100 : 100;

  // 마우스/터치 이벤트에서 clientX 추출
  const getClientX = (e) => {
    if (e.touches && e.touches.length > 0) {
      return e.touches[0].clientX;
    }
    if (e.changedTouches && e.changedTouches.length > 0) {
      return e.changedTouches[0].clientX;
    }
    return e.clientX;
  };

  const handlePointerDown = (type) => (e) => {
    e.preventDefault();
    const rect = sliderRef.current?.getBoundingClientRect();
    if (!rect) return;

    setIsDragging(true);
    setDraggingStart(startDate);
    setDraggingEnd(endDate);

    let lastStartDate = startDate;
    let lastEndDate = endDate;

    const handleMove = (moveEvent) => {
      const clientX = getClientX(moveEvent);
      const percent = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
      const dayNum = Math.round(minNum + (percent / 100) * totalDays);
      const newDate = numberToDate(dayNum);

      if (type === 'start') {
        const currentEndNum = dateToNumber(lastEndDate) || maxNum;
        if (dayNum <= currentEndNum) {
          lastStartDate = newDate;
          setDraggingStart(newDate);
        }
      } else {
        const currentStartNum = dateToNumber(lastStartDate) || minNum;
        if (dayNum >= currentStartNum) {
          lastEndDate = newDate;
          setDraggingEnd(newDate);
        }
      }
    };

    const handleEnd = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);

      setIsDragging(false);

      if (type === 'start') {
        onStartDateChange(lastStartDate);
      } else {
        onEndDateChange(lastEndDate);
      }

      if (onDragEnd) {
        onDragEnd(lastStartDate, lastEndDate);
      }

      setDraggingStart(null);
      setDraggingEnd(null);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  };

  const handleBarPointerDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const rect = sliderRef.current?.getBoundingClientRect();
    if (!rect) return;

    const currentStartNum = dateToNumber(startDate) || minNum;
    const currentEndNum = dateToNumber(endDate) || maxNum;
    const rangeDays = currentEndNum - currentStartNum;

    const startX = getClientX(e);
    const startPercentVal = ((startX - rect.left) / rect.width) * 100;
    const startDayOffset = Math.round((startPercentVal / 100) * totalDays);

    setIsDragging(true);
    setDraggingStart(startDate);
    setDraggingEnd(endDate);

    let lastStartDate = startDate;
    let lastEndDate = endDate;

    const handleMove = (moveEvent) => {
      const clientX = getClientX(moveEvent);
      const currentPercent = ((clientX - rect.left) / rect.width) * 100;
      const currentDayOffset = Math.round((currentPercent / 100) * totalDays);
      const dayDelta = currentDayOffset - startDayOffset;

      let newStartNum = currentStartNum + dayDelta;
      let newEndNum = currentEndNum + dayDelta;

      if (newStartNum < minNum) {
        newStartNum = minNum;
        newEndNum = minNum + rangeDays;
      }
      if (newEndNum > maxNum) {
        newEndNum = maxNum;
        newStartNum = maxNum - rangeDays;
      }

      const newStartDate = numberToDate(newStartNum);
      const newEndDate = numberToDate(newEndNum);

      lastStartDate = newStartDate;
      lastEndDate = newEndDate;
      setDraggingStart(newStartDate);
      setDraggingEnd(newEndDate);
    };

    const handleEnd = () => {
      document.removeEventListener('mousemove', handleMove);
      document.removeEventListener('mouseup', handleEnd);
      document.removeEventListener('touchmove', handleMove);
      document.removeEventListener('touchend', handleEnd);

      setIsDragging(false);

      onStartDateChange(lastStartDate);
      onEndDateChange(lastEndDate);

      if (onDragEnd) {
        onDragEnd(lastStartDate, lastEndDate);
      }

      setDraggingStart(null);
      setDraggingEnd(null);
    };

    document.addEventListener('mousemove', handleMove);
    document.addEventListener('mouseup', handleEnd);
    document.addEventListener('touchmove', handleMove, { passive: false });
    document.addEventListener('touchend', handleEnd);
  };

  const handleInputChange = (type, value) => {
    if (!value) {
      if (type === 'start') {
        onStartDateChange(null);
        if (onDragEnd) onDragEnd(null, endDate);
      } else {
        onEndDateChange(null);
        if (onDragEnd) onDragEnd(startDate, null);
      }
      return;
    }

    const inputNum = dateToNumber(value);
    if (type === 'start') {
      if (inputNum >= minNum && inputNum <= maxNum && (!endDate || inputNum <= dateToNumber(endDate))) {
        onStartDateChange(value);
        if (onDragEnd) onDragEnd(value, endDate);
      }
    } else {
      if (inputNum >= minNum && inputNum <= maxNum && (!startDate || inputNum >= dateToNumber(startDate))) {
        onEndDateChange(value);
        if (onDragEnd) onDragEnd(startDate, value);
      }
    }
  };

  const hasDateFilter = startDate || endDate;

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-[7.2px]">
      <div className="mb-2 flex items-center justify-between gap-6">
        <div className="flex shrink-0 items-center gap-2">
          <Calendar className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium text-gray-700">기간 설정</span>
          {hasDateFilter && (
            <span className="rounded-full bg-primary/10 px-2 py-0.5 text-xs text-primary">
              필터 적용됨
            </span>
          )}
        </div>
        <div className="flex flex-1 items-center gap-2">
          <div className="flex h-[25px] flex-1 items-center gap-4">
            <div className="flex h-[25px] flex-1 items-center gap-1">
              <label className="shrink-0 text-xs text-gray-500">시작</label>
              <input
                type="date"
                value={compact ? (displayStartDate || minDate) : (startDate || '')}
                min={minDate}
                max={endDate || maxDate}
                onChange={(e) => handleInputChange('start', e.target.value)}
                className="h-[25px] flex-1 min-w-0 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
              />
            </div>
            <span className="shrink-0 text-gray-400">~</span>
            <div className="flex h-[25px] flex-1 items-center gap-1">
              <label className="shrink-0 text-xs text-gray-500">종료</label>
              <input
                type="date"
                value={compact ? (displayEndDate || maxDate) : (endDate || '')}
                min={startDate || minDate}
                max={maxDate}
                onChange={(e) => handleInputChange('end', e.target.value)}
                className="h-[25px] flex-1 min-w-0 rounded-lg border border-gray-300 bg-white px-2 text-sm text-gray-900 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary/20"
              />
            </div>
          </div>
          {isLoading && (
            <div className="h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          )}
          {hasDateFilter && (
            <button
              onClick={onReset}
              className="flex shrink-0 items-center gap-1 rounded-lg px-2 py-1 text-xs text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-700"
              title="전체 기간으로 초기화"
            >
              <RotateCcw className="h-3 w-3" />
              <span>초기화</span>
            </button>
          )}
        </div>
      </div>

      <div className={compact ? 'flex items-center gap-2' : 'mb-2'}>
        {/* 전체 범위 표시 - 좌우 상단 (compact 는 막대 title 로) */}
        {!compact && (
          <div className="flex justify-between text-[10px] text-gray-400 px-2">
            <span>{formatDate(minDate)}</span>
            <span>{formatDate(maxDate)}</span>
          </div>
        )}
        <div ref={sliderRef} className={`relative select-none ${compact ? 'mx-2 h-5 min-w-0 flex-1' : 'mx-auto h-8 w-[calc(100%-16px)]'}`}>
          <div
            className="absolute left-0 right-0 top-1/2 h-2 -translate-y-1/2 rounded-full bg-gray-200"
            title={compact ? `전체 기간 ${formatDate(minDate)} ~ ${formatDate(maxDate)}` : undefined}
          />
          <div
            className="absolute top-1/2 h-3 -translate-y-1/2 cursor-move rounded-full bg-primary/40 transition-colors hover:bg-primary/50"
            style={{
              left: `${startPercent}%`,
              width: `${Math.max(endPercent - startPercent, 1)}%`,
            }}
            onMouseDown={handleBarPointerDown}
            onTouchStart={handleBarPointerDown}
            title="드래그하여 범위 이동"
          >
            {/* 선택된 기간 일수 표시 (compact 는 슬라이더 오른쪽 끝) */}
            {!compact && (
              <div className="absolute -top-4 left-1/2 -translate-x-1/2 whitespace-nowrap text-[10px] font-medium text-primary">
                {endNum - startNum + 1}일
              </div>
            )}
          </div>
          <div
            className="absolute top-1/2 z-10 h-5 w-5 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border-2 border-primary bg-white shadow-md transition-shadow hover:shadow-lg active:cursor-grabbing"
            style={{ left: `${startPercent}%` }}
            onMouseDown={handlePointerDown('start')}
            onTouchStart={handlePointerDown('start')}
            title={`시작: ${formatDate(displayStartDate || minDate)}`}
          >
            {!compact && (
              <div className="absolute -bottom-5 left-1/2 whitespace-nowrap text-[10px] text-gray-500">
                {formatDate(displayStartDate || minDate)}
              </div>
            )}
          </div>
          <div
            className="absolute top-1/2 z-10 h-5 w-5 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border-2 border-red-400 bg-white shadow-md transition-shadow hover:shadow-lg active:cursor-grabbing"
            style={{ left: `${endPercent}%` }}
            onMouseDown={handlePointerDown('end')}
            onTouchStart={handlePointerDown('end')}
            title={`종료: ${formatDate(displayEndDate || maxDate)}`}
          >
            {!compact && (
              <div className="absolute -bottom-5 left-1/2 -translate-x-full whitespace-nowrap text-[10px] text-gray-500">
                {formatDate(displayEndDate || maxDate)}
              </div>
            )}
          </div>
        </div>
        {/* compact 의 일수 — 입력칸 줄은 학습 화면 폭에서 이미 넘쳐 거기 두면 가려진다, 그래서 슬라이더 오른쪽 끝 */}
        {compact && (
          <span className="shrink-0 whitespace-nowrap text-[10px] font-medium text-primary">
            {endNum - startNum + 1}일
          </span>
        )}
      </div>

    </div>
  );
};

/**
 * AITrackerContent 컴포넌트
 * @param {Object} props
 * @param {number} props.studentId - 학생 ID
 * @param {Object} props.studentInfo - 학생 정보 (student_name, school_name, school_grade 등)
 * @param {string} props.activeTab - 현재 활성 탭 ('ai' | 'engine' | 'rawdata')
 * @param {function} props.onTabChange - 탭 변경 콜백
 * @param {React.ReactNode} [props.stackedTableHeader] - (stackChartTable 전용 · 학습 화면만) 차트 아래 표 칸에서 표 위에 두는 머리 띠
 * @param {React.ReactNode} [props.stackedTableAlt] - (stackChartTable 전용 · 학습 화면만) 주면 머리 띠·표 대신 같은 칸에 그리는 대체 보기
 * @param {boolean} [props.compactDateRange] - (학습 화면만) 기간 설정 칸을 줄인다 — 날짜는 입력칸에만, 슬라이더 위아래 날짜 글자 줄 없음
 * @param {boolean} [props.includeV2Assignments] - (학습 v2만) 추이 시점 문제에 마감된 v2 배정 결과를 합친다
 */
export function AITrackerContent({ studentId, studentInfo, activeTab = 'ai', onTabChange, onEngineDataLoad, onGeneticsDataLoad, hideStudentInfo = false, stackChartTable = false, hideEngineSummary = false, onConceptSelect, onConceptsSelect, selectedConceptCids = [], useV2Preview = false, includeV2Assignments = false, stackedTableHeader = null, stackedTableAlt = null, compactDateRange = false }) {
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();

  // 탭 상태: props로 전달되면 controlled mode, 아니면 uncontrolled mode
  const [internalTab, setInternalTab] = useState(activeTab);
  const currentTab = onTabChange ? activeTab : internalTab;
  const setCurrentTab = onTabChange ? onTabChange : setInternalTab;

  // 상태
  const [geneticsData, setGeneticsData] = useState(null);
  const [isLoadingGenetics, setIsLoadingGenetics] = useState(false);
  const [hasInitialLoaded, setHasInitialLoaded] = useState(false); // 최초 로드 완료 여부
  const [mathConcepts, setMathConcepts] = useState([]);
  const [error, setError] = useState(null);
  const [showAllConcepts, setShowAllConcepts] = useState(false);
  const [showTypeFilter, setShowTypeFilter] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedMathTypes, setSelectedMathTypes] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);  // 수학개념 테이블 태그 분류 필터(첫 태그 기준)
  const [showSelectedOnly, setShowSelectedOnly] = useState(false); // 차트 옆 개념 테이블: 선택된 개념만 표시 토글
  // AI 탭 필터 상태
  const [showFilterPanel, setShowFilterPanel] = useState(false); // 차트 뷰 플로팅 필터 드로어 열림 여부
  const [stabilityRange, setStabilityRange] = useState([0, 1]); // [min, max]
  const [showDelta, setShowDelta] = useState(false); // delta 모드
  const [useStabilityY, setUseStabilityY] = useState(false); // Y축에 stability 사용
  // 초기 디폴트(r=0·h=0·st=0.5, 학습 기록 미반영) 개념 숨김 — 기본 ON, 토글로 되돌릴 수 있음
  const [hideDefaults, setHideDefaults] = useState(true);
  // AI 탭 뷰 모드 상태
  const [aiViewMode, setAiViewMode] = useState('chart'); // 'table' | 'chart'
  const [showTrendModal, setShowTrendModal] = useState(false); // 개념 추이(직접 개념 r 시계열) 모달
  const [rTableSort, setRTableSort] = useState({ column: null, direction: null });
  const [hTableSort, setHTableSort] = useState({ column: null, direction: null });
  const [stTableSort, setStTableSort] = useState({ column: null, direction: null });
  const [chartSideTableSort, setChartSideTableSort] = useState({ column: null, direction: null });
  // r/h/st 범위 필터 상태 (테이블 공유)
  const [rRange, setRRange] = useState([-1, 1]);
  const [hRange, setHRange] = useState([-1, 1]);
  const [stRange, setStRange] = useState([0, 1]);
  const [columnFilterPopup, setColumnFilterPopup] = useState(null); // { column, rect, sortState, setSortFn }
  const [selectedPoint, setSelectedPoint] = useState(null); // 선택된 점의 cid
  const scatterContainerRef = useRef(null);
  const resizeTimerRef = useRef(null);
  const chartSideTableRef = useRef(null);   // 차트 옆 개념 테이블 스크롤 컨테이너
  // 좌표평면 드래그(rubber-band) 다중 선택
  const [dragRect, setDragRect] = useState(null); // {x0,y0,x1,y1} 오버레이 px (드래그 중에만 non-null)
  const dragStateRef = useRef(null);              // { startX, startY, rectLeft, rectTop, moved }
  const didDragRef = useRef(false);               // 드래그 직후 후행 click 1회 억제 플래그
  // 정렬 상태: column(total/d1~d5), criteria(incorrect/count), order(none/asc/desc)
  const [sortColumn, setSortColumn] = useState(null);
  const [sortCriteria, setSortCriteria] = useState(null);
  const [sortOrder, setSortOrder] = useState('none');
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // 날짜 필터 상태
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [dateRange, setDateRange] = useState({ min: null, max: null });
  const [isLoadingDateFilter, setIsLoadingDateFilter] = useState(false);

  // Engine 탭 상태
  const [engineData, setEngineData] = useState(null);
  const [isLoadingEngine, setIsLoadingEngine] = useState(false);
  const [engineError, setEngineError] = useState(null);
  const [engineIncludeMetadata, setEngineIncludeMetadata] = useState(false);
  const [engineForceReloadK, setEngineForceReloadK] = useState(false);
  // Engine 테이블 정렬 상태: { column: string | null, direction: 'asc' | 'desc' | null }
  const [engineTableSort, setEngineTableSort] = useState({ column: null, direction: null });

  // ── 산점도 일별 재생(타임라인) ──
  // playbackIdx: null=라이브(현재 engineData), 정수=프레임 인덱스. 재생은 산점도(차트 뷰) 전용.
  const [playbackIdx, setPlaybackIdx] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackPreloading, setPlaybackPreloading] = useState(false);
  const [playbackProgress, setPlaybackProgress] = useState({ loaded: 0, total: 0 });
  const [timelineDates, setTimelineDates] = useState([]);        // 재생 순서(KST yyyy-mm-dd, 오름차순)
  const [frozenBounds, setFrozenBounds] = useState(null);        // 재생 중 축·크기 동결값(전 프레임 union)
  const framesRef = useRef({});          // { date: {r,h,s} } — 현재 세션 프레임(envelope.frames와 동일 참조)
  const envelopeRef = useRef(null);      // { v, fingerprint, conceptIndex, vScale, dates, frames }
  const playbackGenRef = useRef(0);      // 세대 카운터 — 학생/필터 변경 시 in-flight 응답 폐기
  const playIntervalRef = useRef(null);  // 재생 틱 setInterval 핸들
  // 점 이동 애니메이션 게이팅용 '직전 커밋' 값(렌더에서 현재값과 비교해 '좌표계 변경' 커밋만 하드컷).
  const animPrevIdxRef = useRef(null);
  const animPrevDeltaRef = useRef(false);
  const animPrevStabYRef = useRef(false);
  const animPrevDimsRef = useRef(null);
  const animPrevBoundsRef = useRef(null);
  const animSnapOnceRef = useRef(false); // 다음 1커밋만 하드컷 강제(되감기 재개 등 명시 스냅)
  const [reducedMotion, setReducedMotion] = useState(
    () => typeof window !== 'undefined' && window.matchMedia
      ? window.matchMedia('(prefers-reduced-motion: reduce)').matches : false
  );
  // 범위 필터(r/h/st)는 재생 중 '우회'만 하고 state를 안 바꾸므로 종료 후 자동 보존(스냅샷 불요).

  // 스냅샷 관련 상태
  const [snapshots, setSnapshots] = useState({}); // { conceptId: [순번1, 순번2, ...] }
  const [hasSnapshotted, setHasSnapshotted] = useState(false); // 현재 조회에서 스냅샷 여부

  // 매스컨셉 상세 모달 상태
  const [showMathceptModal, setShowMathceptModal] = useState(false);
  const [selectedConceptId, setSelectedConceptId] = useState(null);

  const handleConceptRowClick = useCallback((cid, name) => {
    // 임베디드(학습 /students-hub) 모드: 모달 대신 상위로 개념 선택을 통지 → 문제 미리보기 필터
    if (onConceptSelect) {
      onConceptSelect(cid, name);
      return;
    }
    setSelectedConceptId(cid);
    setShowMathceptModal(true);
  }, [onConceptSelect]);

  const typeFilterButtonRef = useRef(null);
  const engineCacheRef = useRef(new Map()); // key -> engineData
  const initialLoadDoneRef = useRef(false); // mount 시 engine 이중 호출 방지
  // mount useEffect에서 최신 engine 옵션을 deps 없이 읽기 위한 ref
  const engineOptionsRef = useRef({ includeMetadata: false, forceReloadK: false });
  useEffect(() => { engineOptionsRef.current = { includeMetadata: engineIncludeMetadata, forceReloadK: engineForceReloadK }; }, [engineIncludeMetadata, engineForceReloadK]);

  // LaTeX 렌더링 함수
  const renderLatex = useCallback((text) => {
    if (!text) return '';

    try {
      const latexPattern = /\$([^$]+)\$/g;
      const parts = [];
      let lastIndex = 0;
      let match;

      while ((match = latexPattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
          parts.push(text.substring(lastIndex, match.index));
        }

        const latex = match[1];
        const html = katex.renderToString(latex, {
          throwOnError: false,
          displayMode: false
        });
        parts.push(`<span class="katex-inline">${html}</span>`);

        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex));
      }

      return parts.join('');
    } catch (error) {
      console.error('LaTeX rendering error:', error);
      return text;
    }
  }, []);

  // Math Type 목록
  const mathTypeList = [
    { code: 'AL', name: '대수학' },
    { code: 'AN', name: '해석학' },
    { code: 'GE', name: '기하학' },
    { code: 'PS', name: '확률통계' },
  ];

  // Concept Type 목록
  const conceptTypes = [
    { code: 'DEF', name: '정의' },
    { code: 'AXM', name: '공리' },
    { code: 'IDN', name: '정리' },
    { code: 'THM', name: '이론' },
    { code: 'MTH', name: '방법론' },
    { code: 'PRP', name: '성질' },
    { code: 'FOR', name: '공식' },
    { code: 'NTN', name: '표기법' },
  ];

  // 필터 토글 함수들 (필터 변경 시 스냅샷 초기화)
  const toggleConceptTypeFilter = (typeCode) => {
    setSelectedTypes(prev =>
      prev.includes(typeCode)
        ? prev.filter(t => t !== typeCode)
        : [...prev, typeCode]
    );
    setSnapshots({});
    setHasSnapshotted(false);
  };

  const toggleMathTypeFilter = (typeCode) => {
    setSelectedMathTypes(prev =>
      prev.includes(typeCode)
        ? prev.filter(t => t !== typeCode)
        : [...prev, typeCode]
    );
    setSnapshots({});
    setHasSnapshotted(false);
  };

  const toggleAllConceptTypes = () => {
    if (selectedTypes.length === conceptTypes.length) {
      setSelectedTypes([]);
    } else {
      setSelectedTypes(conceptTypes.map(t => t.code));
    }
    setSnapshots({});
    setHasSnapshotted(false);
  };

  const toggleAllMathTypes = () => {
    if (selectedMathTypes.length === mathTypeList.length) {
      setSelectedMathTypes([]);
    } else {
      setSelectedMathTypes(mathTypeList.map(t => t.code));
    }
    setSnapshots({});
    setHasSnapshotted(false);
  };

  const resetAllFilters = () => {
    setSelectedTypes([]);
    setSelectedMathTypes([]);
    // 필터 초기화 시 스냅샷도 초기화
    setSnapshots({});
    setHasSnapshotted(false);
  };

  const totalSelectedFilters = selectedTypes.length + selectedMathTypes.length;

  // 정렬 핸들러: column(total/d1~d5), criteria(incorrect/count)
  const handleSort = (column, criteria) => {
    // 같은 칼럼, 같은 기준 클릭: none → desc → asc → none 순환
    if (sortColumn === column && sortCriteria === criteria) {
      setSortOrder(prev => {
        if (prev === 'none') return 'desc';
        if (prev === 'desc') return 'asc';
        return 'none';
      });
      // none으로 돌아갈 때 column/criteria 초기화
      if (sortOrder === 'asc') {
        setSortColumn(null);
        setSortCriteria(null);
      }
    } else {
      // 다른 칼럼 또는 다른 기준 클릭: desc로 시작
      setSortColumn(column);
      setSortCriteria(criteria);
      setSortOrder('desc');
    }
    // 정렬 변경 시 스냅샷 초기화
    setSnapshots({});
    setHasSnapshotted(false);
  };

  // 정렬 아이콘 표시 여부 확인
  const getSortIcon = (column, criteria) => {
    if (sortColumn === column && sortCriteria === criteria && sortOrder !== 'none') {
      return sortOrder; // 'asc' or 'desc'
    }
    return null;
  };

  const openTypeFilter = () => {
    if (typeFilterButtonRef.current) {
      const rect = typeFilterButtonRef.current.getBoundingClientRect();
      const buttonCenter = rect.left + (rect.width / 2);
      const dropdownWidth = 320;
      setDropdownPosition({
        top: rect.bottom + 4,
        left: buttonCenter - (dropdownWidth / 2),
      });
    }
    setShowTypeFilter(true);
  };

  // Math Concepts 로드
  useEffect(() => {
    const loadMathConcepts = async () => {
      try {
        const concepts = await fetchMathConcepts();
        setMathConcepts(concepts);
      } catch (err) {
        console.error('Math concepts 로드 실패:', err);
      }
    };
    loadMathConcepts();
  }, []);

  // Genetics 데이터 로드 함수
  const fetchGeneticsData = useCallback(async (sid, startDateParam = null, endDateParam = null, isDateFilterUpdate = false) => {
    try {
      if (isDateFilterUpdate) {
        setIsLoadingDateFilter(true);
      } else {
        setIsLoadingGenetics(true);
      }

      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return null;
      }

      const params = new URLSearchParams({ student_id: sid });
      if (startDateParam) {
        params.append('start_date', startDateParam);
      }
      if (endDateParam) {
        params.append('end_date', endDateParam);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/genetics/students?${params}`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        if (response.status === 400) {
          const errorData = await response.json();
          throw new Error(errorData.message || '유효하지 않은 날짜 형식입니다.');
        }
        if (response.status === 403) {
          /**
           * 종전 문구는 「접근 권한이 없습니다. Manager 권한이 필요합니다.」였다 — 2026-09-10 로
           * **거짓이 됐다**(교사도 이 화면을 지난다. 회신 §4-2 는 genetics 3종이 애초에
           * `user_required` 였다고 코드로 확인해 줬고, §5 는 이 단정을 지우라고 적었다).
           *
           * 사유는 봉투 `data.reason` 에 온다(§1-3) — 여기까지는 403 에서 본문을 열지 않아
           * 「담당 범위 밖」과 「이 계정이 아니다」가 한 문구로 뭉쳐 있었다. 문구 자체는
           * utils/forbiddenText 가 갖는다: 이 파일 안에서만 같은 403 을 두 자리에서 다루므로
           * 문구를 여기 적으면 그 둘이 갈린다.
           *
           * ⚠ **이 갈래는 화면에 뜨지 않는다.** `error` 상태(setError)를 읽는 JSX 가 이 파일에
           * 없다 — 값만 쌓이고 그리는 자리가 없다(형제인 engine 쪽 `engineError` 는 그려진다).
           * 문구를 고친 효과는 여기서 눈에 보이지 않으니, 고쳤다고 화면이 달라졌다고 말하지 마라.
           * error 를 새로 그리는 것은 이번 범위가 아니다(오너 지시).
           */
          const body = await response.json().catch(() => null);
          throw new Error(forbiddenText(body?.data?.reason));
        }
        throw new Error('테스트 데이터가 없습니다.');
      }

      const result = await response.json();
      setError(null); // 성공 시 에러 상태 초기화
      setHasSnapshotted(false); // 새 데이터 조회 시 스냅샷 버튼 활성화
      return result.data;
    } catch (err) {
      console.error('Genetics 데이터 로드 오류:', err);
      setError(err.message);
      return null;
    } finally {
      if (isDateFilterUpdate) {
        setIsLoadingDateFilter(false);
      } else {
        setIsLoadingGenetics(false);
      }
    }
  }, [getAccessToken, navigate]);

  // stamps 데이터에서 날짜 범위 추출
  const extractDateRange = useCallback((data) => {
    if (!data?.stamps || data.stamps.length === 0) {
      return { min: null, max: null };
    }

    let minDate = null;
    let maxDate = null;

    data.stamps.forEach(stamp => {
      stamp.exams?.forEach(exam => {
        // KST 기준으로 날짜 산출 — 재생 타임라인(kstDateOf)·백엔드 필터(end_date=KST 해석)와 동일 달력.
        // (기존 split('T')[0]=naive UTC는 저녁 UTC 시험을 하루 어긋나게 해 슬라이더/타임라인이 불일치했음)
        const examDate = kstDateOf(exam.updated_at);
        if (examDate) {
          if (!minDate || examDate < minDate) minDate = examDate;
          if (!maxDate || examDate > maxDate) maxDate = examDate;
        }
      });
    });

    return { min: minDate, max: maxDate };
  }, []);

  // 날짜 변경 핸들러
  const handleStartDateChange = useCallback((newDate) => {
    setStartDate(newDate);
  }, []);

  const handleEndDateChange = useCallback((newDate) => {
    setEndDate(newDate);
  }, []);

  const fetchEngineData = useCallback(async (sid, startDateParam = null, endDateParam = null, isDateFilterUpdate = false, options = {}) => {
    const { includeMetadata = false, forceReloadK = false } = options;
    try {
      if (isDateFilterUpdate) {
        setIsLoadingDateFilter(true);
      } else {
        setIsLoadingEngine(true);
      }

      const token = await getAccessToken();
      if (!token) {
        navigate('/signin');
        return null;
      }

      const params = new URLSearchParams({ student_id: sid });
      if (startDateParam) params.append('start_date', startDateParam);
      if (endDateParam) params.append('end_date', endDateParam);
      if (includeMetadata) params.append('metadata', '1');
      if (forceReloadK) params.append('k_force_reload', '1');

      const cacheKey = `${sid}|${startDateParam || ''}|${endDateParam || ''}|${includeMetadata ? '1' : '0'}`;
      if (!forceReloadK && engineCacheRef.current.has(cacheKey)) {
        setEngineError(null);
        return engineCacheRef.current.get(cacheKey);
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/genetics/engine?${params}`,
        {
          headers: {
            'Authorization': token,
            'Content-Type': 'application/json',
          }
        }
      );

      if (!response.ok) {
        if (response.status === 400) {
          const errorData = await response.json();
          throw new Error(errorData.message || '유효하지 않은 날짜/파라미터입니다.');
        }
        if (response.status === 403) {
          /**
           * 위 genetics/students 갈래와 **같은 이유·같은 문구**다(회신 §4-2·§5) — 그래서 문구를
           * 이 파일에 적지 않고 utils/forbiddenText 에서 가져온다. 같은 403 을 한 파일 안 두
           * 자리에서 각자 적어 두면 한쪽만 고쳐지는 날이 온다.
           *
           * 이쪽은 **화면에 뜬다** — `engineError` 를 읽는 JSX 가 engine 탭 안에 있다.
           * 교사에게 실제로 보이는 문구는 여기서 나온 것이다.
           */
          const body = await response.json().catch(() => null);
          throw new Error(forbiddenText(body?.data?.reason));
        }
        throw new Error('Engine 데이터를 불러오는데 실패했습니다.');
      }

      const result = await response.json();
      setEngineError(null);
      engineCacheRef.current.set(cacheKey, result.data);
      return result.data;
    } catch (err) {
      console.error('Engine 데이터 로드 오류:', err);
      setEngineError(err.message);
      return null;
    } finally {
      if (isDateFilterUpdate) {
        setIsLoadingDateFilter(false);
      } else {
        setIsLoadingEngine(false);
      }
    }
  }, [getAccessToken, navigate]);

  // 재생 프레임 전용 silent fetch — 로딩 플래그(isLoadingEngine/DateFilter)·setEngineError를 건드리지 않아
  // 차트가 프레임마다 스피너로 깜빡이거나 engine 탭에 에러가 새지 않게 한다. {data, error} 반환.
  // engineCacheRef(metadata=0 키)를 재사용 → 일반 조회/프레임이 서로 캐시를 덥힌다.
  // 지금은 옛 방식(날짜마다 한 번) — 아래 fetchEngineTimeline 이 안 될 때만 쓴다.
  const fetchEngineFrame = useCallback(async (sid, startDateParam, endDateParam) => {
    const cacheKey = `${sid}|${startDateParam || ''}|${endDateParam || ''}|0`;
    if (engineCacheRef.current.has(cacheKey)) {
      return { data: engineCacheRef.current.get(cacheKey), error: null };
    }
    try {
      const token = await getAccessToken();
      if (!token) return { data: null, error: 'no-token' };
      const params = new URLSearchParams({ student_id: sid });
      if (startDateParam) params.append('start_date', startDateParam);
      if (endDateParam) params.append('end_date', endDateParam);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/genetics/engine?${params}`,
        { headers: { Authorization: token, 'Content-Type': 'application/json' } }
      );
      if (!response.ok) return { data: null, error: `http-${response.status}` };
      const result = await response.json();
      engineCacheRef.current.set(cacheKey, result.data);
      return { data: result.data, error: null };
    } catch (err) {
      return { data: null, error: err.message || 'fetch-failed' };
    }
  }, [getAccessToken]);

  // 재생·추이 프레임을 한 번에 — GET /api/genetics/engine/timeline(BE-20260915 회신, dev 8b60dcd).
  // 서버가 시험을 한 번만 적용하며 시험 본 KST 날마다 「그날 마지막 시험 적용 직후」 r/h/s 를 모아 준다.
  // fetchEngineFrame 과 같이 silent(로딩 플래그·engineError 무관), {data, error} 반환. 인자·400·422 는 /engine 과 같다.
  const fetchEngineTimeline = useCallback(async (sid, startDateParam, endDateParam) => {
    try {
      const token = await getAccessToken();
      if (!token) return { data: null, error: 'no-token' };
      const params = new URLSearchParams({ student_id: sid });
      if (startDateParam) params.append('start_date', startDateParam);
      if (endDateParam) params.append('end_date', endDateParam);
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/genetics/engine/timeline?${params}`,
        { headers: { Authorization: token, 'Content-Type': 'application/json' } }
      );
      if (!response.ok) return { data: null, error: `http-${response.status}` };
      const result = await response.json();
      return { data: result.data, error: null };
    } catch (err) {
      return { data: null, error: err.message || 'fetch-failed' };
    }
  }, [getAccessToken]);

  const handleDragEnd = useCallback(async (newStartDate, newEndDate) => {
    if (!studentId) return;

    if (currentTab === 'engine' || currentTab === 'ai') {
      const data = await fetchEngineData(
        studentId,
        newStartDate,
        newEndDate,
        true,
        { includeMetadata: engineIncludeMetadata, forceReloadK: engineForceReloadK }
      );
      if (data) setEngineData(data);
      return;
    }

    // rawdata 탭(기존)
    const data = await fetchGeneticsData(studentId, newStartDate, newEndDate, true);
    if (data) setGeneticsData(data);
  }, [studentId, currentTab, fetchGeneticsData, fetchEngineData, engineIncludeMetadata, engineForceReloadK]);

  const handleDateReset = useCallback(async () => {
    setStartDate(null);
    setEndDate(null);

    if (!studentId) return;

    if (currentTab === 'engine' || currentTab === 'ai') {
      const data = await fetchEngineData(
        studentId,
        null,
        null,
        true,
        { includeMetadata: engineIncludeMetadata, forceReloadK: engineForceReloadK }
      );
      if (data) setEngineData(data);
      return;
    }

    if (currentTab === 'rawdata') {
      const data = await fetchGeneticsData(studentId);
      if (data) setGeneticsData(data);
    }
  }, [studentId, currentTab, fetchGeneticsData, fetchEngineData, engineIncludeMetadata, engineForceReloadK]);

  // 컴포넌트 마운트 시 데이터 로드 (genetics + engine 병렬)
  useEffect(() => {
    if (studentId) {
      initialLoadDoneRef.current = false;
      const loadData = async () => {
        setError(null);
        setEngineError(null);
        setStartDate(null);
        setEndDate(null);
        setCurrentTab('ai');
        engineCacheRef.current.clear();
        setEngineData(null);
        setHasInitialLoaded(false);
        setRRange([-1, 1]);
        setHRange([-1, 1]);
        setStRange([0, 1]);

        const opts = engineOptionsRef.current;
        const [gData, eData] = await Promise.all([
          fetchGeneticsData(studentId),
          fetchEngineData(studentId, null, null, false,
            { includeMetadata: opts.includeMetadata, forceReloadK: opts.forceReloadK }),
        ]);

        if (gData) {
          setGeneticsData(gData);
          const range = extractDateRange(gData);
          setDateRange(range);
        } else {
          setGeneticsData(null);
          setDateRange({ min: null, max: null });
        }
        if (eData) setEngineData(eData);

        initialLoadDoneRef.current = true;
        setHasInitialLoaded(true);
      };
      loadData();
    }
  }, [studentId, fetchGeneticsData, fetchEngineData, extractDateRange]);

  // 탭 전환 / 날짜 변경 시 필요한 데이터 lazy-load
  const prevStudentIdRef = useRef(studentId);
  useEffect(() => {
    if (!studentId) return;

    // studentId가 변경된 경우 mount useEffect가 처리하므로 skip
    if (prevStudentIdRef.current !== studentId) {
      prevStudentIdRef.current = studentId;
      return;
    }

    // mount 직후 첫 fire는 skip (mount useEffect에서 이미 호출)
    if (!initialLoadDoneRef.current) return;

    if (currentTab === 'engine' || currentTab === 'ai') {
      const load = async () => {
        const data = await fetchEngineData(
          studentId,
          startDate,
          endDate,
          false,
          { includeMetadata: engineIncludeMetadata, forceReloadK: engineForceReloadK }
        );
        if (data) setEngineData(data);
      };
      load();
    }
  }, [currentTab, studentId, startDate, endDate, engineIncludeMetadata, engineForceReloadK, fetchEngineData]);

  // engineData가 변경되면 부모 컴포넌트에 전달
  useEffect(() => {
    if (engineData && onEngineDataLoad) {
      onEngineDataLoad(engineData);
    }
  }, [engineData, onEngineDataLoad]);

  // geneticsData가 변경되면 부모 컴포넌트에 전달
  useEffect(() => {
    if (onGeneticsDataLoad) {
      onGeneticsDataLoad(geneticsData);
    }
  }, [geneticsData, onGeneticsDataLoad]);

  // concept_type 추출 헬퍼 함수
  const extractConceptType = useCallback((concept) => {
    if (concept?.concept_type) return concept.concept_type;
    if (concept?.text && concept.text.includes(':')) {
      const prefix = concept.text.split(':')[0];
      if (prefix.includes('-')) {
        return prefix.split('-')[1];
      }
    }
    return null;
  }, []);

  // mathConcepts를 Map으로 변환 (O(1) 조회용) + LaTeX 캐시 동기 클리어
  const latexCacheRef = useRef(new Map());
  const mathConceptsMap = useMemo(() => {
    latexCacheRef.current.clear(); // useMemo 내에서 동기적으로 클리어
    const map = new Map();
    mathConcepts.forEach(c => map.set(String(c.id), c));
    return map;
  }, [mathConcepts]);

  const getConceptDisplayName = useCallback((cid) => {
    const cached = latexCacheRef.current.get(cid);
    if (cached) return cached;

    const concept = mathConceptsMap.get(String(cid));
    const raw = concept?.text || `개념 ${cid}`;
    const display = raw.includes(':') ? raw.split(':').slice(1).join(':').trim() : raw;
    const result = { concept, rendered: renderLatex(display) };
    latexCacheRef.current.set(cid, result);
    return result;
  }, [mathConceptsMap, renderLatex]);

  const decodeQ = (q, scale) => {
    if (q === null || q === undefined) return null;
    if (!scale) return q;
    return q / scale;
  };

  // 1단계: 기본 테이블 데이터 변환 (API 데이터 변경 시만 재계산)
  const baseTableData = useMemo(() => {
    if (currentTab !== 'rawdata') return [];
    if (!geneticsData?.concepts && !showAllConcepts) return [];

    if (showAllConcepts && mathConcepts.length > 0) {
      return mathConcepts.map(concept => {
        const conceptId = String(concept.id);
        const data = geneticsData?.concepts?.[conceptId] || [[0, 0], [0, 0], [0, 0], [0, 0], [0, 0], [0, 0]];
        const conceptName = concept.text || `개념 ${conceptId}`;

        return {
          conceptId,
          conceptName,
          conceptOrder: concept.order || parseInt(conceptId),
          conceptType: extractConceptType(concept),
          mathType: concept.math_type || null,
          total: data[0] || [0, 0],
          difficulty1: data[1] || [0, 0],
          difficulty2: data[2] || [0, 0],
          difficulty3: data[3] || [0, 0],
          difficulty4: data[4] || [0, 0],
          difficulty5: data[5] || [0, 0],
        };
      });
    }

    if (geneticsData?.concepts) {
      return Object.entries(geneticsData.concepts).map(([conceptId, data]) => {
        const concept = mathConceptsMap.get(conceptId); // O(1) 조회
        const conceptName = concept?.text || `개념 ${conceptId}`;

        return {
          conceptId,
          conceptName,
          conceptOrder: concept?.order || parseInt(conceptId),
          conceptType: extractConceptType(concept),
          mathType: concept?.math_type || null,
          total: data[0] || [0, 0],
          difficulty1: data[1] || [0, 0],
          difficulty2: data[2] || [0, 0],
          difficulty3: data[3] || [0, 0],
          difficulty4: data[4] || [0, 0],
          difficulty5: data[5] || [0, 0],
        };
      });
    }

    return [];
  }, [currentTab, geneticsData, mathConcepts, mathConceptsMap, showAllConcepts, extractConceptType]);

  // 2단계: 필터 + 정렬 적용 (필터/정렬 변경 시만 재계산)
  const tableData = useMemo(() => {
    let result = baseTableData;

    // Math Type 필터 적용
    if (selectedMathTypes.length > 0) {
      result = result.filter(row => row.mathType && selectedMathTypes.includes(row.mathType));
    }

    // Concept Type 필터 적용
    if (selectedTypes.length > 0) {
      result = result.filter(row => row.conceptType && selectedTypes.includes(row.conceptType));
    }

    // 정렬 적용
    if (sortColumn && sortOrder !== 'none') {
      const getDataByColumn = (row) => {
        switch(sortColumn) {
          case 'total': return row.total;
          case 'd1': return row.difficulty1;
          case 'd2': return row.difficulty2;
          case 'd3': return row.difficulty3;
          case 'd4': return row.difficulty4;
          case 'd5': return row.difficulty5;
          default: return row.total;
        }
      };

      return [...result].sort((a, b) => {
        const dataA = getDataByColumn(a);
        const dataB = getDataByColumn(b);

        let valueA, valueB;
        if (sortCriteria === 'incorrect') {
          valueA = dataA[1];
          valueB = dataB[1];
        } else if (sortCriteria === 'rate') {
          const totalA = dataA[0] + dataA[1];
          const totalB = dataB[0] + dataB[1];
          valueA = totalA > 0 ? (dataA[1] / totalA) * 100 : 0;
          valueB = totalB > 0 ? (dataB[1] / totalB) * 100 : 0;
        } else {
          valueA = dataA[0] + dataA[1];
          valueB = dataB[0] + dataB[1];
        }

        return sortOrder === 'asc' ? valueA - valueB : valueB - valueA;
      });
    }

    return [...result].sort((a, b) => a.conceptOrder - b.conceptOrder);
  }, [baseTableData, selectedTypes, selectedMathTypes, sortColumn, sortCriteria, sortOrder]);

  // 스냅샷 버튼 핸들러 (tableData 정의 이후에 위치해야 함)
  const handleSnapshot = useCallback(() => {
    if (hasSnapshotted || tableData.length === 0) return;

    const newSnapshots = { ...snapshots };
    tableData.forEach((row, index) => {
      const conceptId = row.conceptId;
      if (!newSnapshots[conceptId]) {
        newSnapshots[conceptId] = [];
      }
      newSnapshots[conceptId].push(index + 1); // 1부터 시작하는 순번
    });

    setSnapshots(newSnapshots);
    setHasSnapshotted(true);
  }, [hasSnapshotted, tableData, snapshots]);

  // 최대값 계산
  const maxTotal = useMemo(() => {
    if (tableData.length === 0) return 1;
    return Math.max(...tableData.map(row => row.total[0] + row.total[1]), 1);
  }, [tableData]);

  const maxDifficultyTotal = useMemo(() => {
    if (tableData.length === 0) return 1;

    let max = 1;
    tableData.forEach(row => {
      max = Math.max(max,
        row.difficulty1[0] + row.difficulty1[1],
        row.difficulty2[0] + row.difficulty2[1],
        row.difficulty3[0] + row.difficulty3[1],
        row.difficulty4[0] + row.difficulty4[1],
        row.difficulty5[0] + row.difficulty5[1]
      );
    });
    return max;
  }, [tableData]);

  // 오답률 계산
  const errorRates = useMemo(() => {
    if (tableData.length === 0) {
      return { total: null, d1: null, d2: null, d3: null, d4: null, d5: null };
    }

    let totalCorrect = 0, totalIncorrect = 0;
    let d1Correct = 0, d1Incorrect = 0;
    let d2Correct = 0, d2Incorrect = 0;
    let d3Correct = 0, d3Incorrect = 0;
    let d4Correct = 0, d4Incorrect = 0;
    let d5Correct = 0, d5Incorrect = 0;

    tableData.forEach(row => {
      totalCorrect += row.total[0];
      totalIncorrect += row.total[1];
      d1Correct += row.difficulty1[0];
      d1Incorrect += row.difficulty1[1];
      d2Correct += row.difficulty2[0];
      d2Incorrect += row.difficulty2[1];
      d3Correct += row.difficulty3[0];
      d3Incorrect += row.difficulty3[1];
      d4Correct += row.difficulty4[0];
      d4Incorrect += row.difficulty4[1];
      d5Correct += row.difficulty5[0];
      d5Incorrect += row.difficulty5[1];
    });

    const calcRate = (correct, incorrect) => {
      const total = correct + incorrect;
      if (total === 0) return null;
      return ((incorrect / total) * 100).toFixed(1);
    };

    return {
      total: { rate: calcRate(totalCorrect, totalIncorrect), incorrect: totalIncorrect, count: totalCorrect + totalIncorrect },
      d1: { rate: calcRate(d1Correct, d1Incorrect), incorrect: d1Incorrect, count: d1Correct + d1Incorrect },
      d2: { rate: calcRate(d2Correct, d2Incorrect), incorrect: d2Incorrect, count: d2Correct + d2Incorrect },
      d3: { rate: calcRate(d3Correct, d3Incorrect), incorrect: d3Incorrect, count: d3Correct + d3Incorrect },
      d4: { rate: calcRate(d4Correct, d4Incorrect), incorrect: d4Incorrect, count: d4Correct + d4Incorrect },
      d5: { rate: calcRate(d5Correct, d5Incorrect), incorrect: d5Incorrect, count: d5Correct + d5Incorrect },
    };
  }, [tableData]);

  // 개념명 LaTeX 렌더링 캐시 (tableData 변경 시에만 재계산)
  const renderedConceptNames = useMemo(() => {
    const map = new Map();
    tableData.forEach(row => {
      const displayName = row.conceptName.includes(':')
        ? row.conceptName.split(':').slice(1).join(':').trim()
        : row.conceptName;
      map.set(row.conceptId, renderLatex(displayName));
    });
    return map;
  }, [tableData, renderLatex]);

  // 재생 중이면 프레임 데이터로 갈아끼운 engineData 형태를 반환(그 외엔 라이브). 스왑 지점은 여기 하나 —
  // 요약바·학생 헤더·onEngineDataLoad는 라이브 engineData 유지(프레임이 부모/요약으로 새지 않게).
  // 슬림 프레임엔 last_record/total_record/avg_diff가 없어 []로 두면 engineTableRows가 폴백한다.
  const displayEngineData = useMemo(() => {
    if (playbackIdx == null || !envelopeRef.current) return engineData;
    const env = envelopeRef.current;
    const date = timelineDates[playbackIdx];
    const frame = date ? framesRef.current[date] : null;
    if (!frame || !engineData) return engineData;
    const prev = playbackIdx > 0 ? framesRef.current[timelineDates[playbackIdx - 1]] : null;
    return {
      ...engineData,
      concept_index: env.conceptIndex,
      v_scale: env.vScale,
      r_status: frame.r,
      h_status: frame.h,
      stability: frame.s,
      delta: frameDelta(frame, prev, env.vScale), // {delta_r_status, delta_h_status, delta_stability}
      last_record: [],
      total_record: [],
      avg_diff: [],
      avg_diff_n: [],
    };
  }, [playbackIdx, timelineDates, engineData]);

  const engineTableRows = useMemo(() => {
    if (!displayEngineData?.concept_index) return [];
    const vScale = displayEngineData.v_scale || 10000;
    const idx = displayEngineData.concept_index;
    const r = displayEngineData.r_status || [];
    const h = displayEngineData.h_status || [];
    const s = displayEngineData.stability || [];
    const dr = displayEngineData.delta?.delta_r_status || [];
    const dh = displayEngineData.delta?.delta_h_status || [];
    const ds = displayEngineData.delta?.delta_stability || [];
    const lastRec = displayEngineData.last_record || [];
    const totalRec = displayEngineData.total_record || [];
    const avgDiff = displayEngineData.avg_diff || [];
    const avgDiffN = displayEngineData.avg_diff_n || [];

    return idx.map((cid, i) => {
      const name = getConceptDisplayName(cid);
      const concept = mathConceptsMap.get(String(cid));
      const lr = lastRec[i];
      const tr = totalRec[i];
      return {
        cid,
        order: concept?.order,
        nameRendered: name.rendered,
        r: decodeQ(r[i], vScale),
        h: decodeQ(h[i], vScale),
        st: decodeQ(s[i], vScale),
        dr: decodeQ(dr[i], vScale),
        dh: decodeQ(dh[i], vScale),
        ds: decodeQ(ds[i], vScale),
        lastRecord: Array.isArray(lr) && lr.length === 2 ? lr : null,
        totalRecord: Array.isArray(tr) && tr.length === 2 ? tr : null,
        avgDiff: avgDiff[i] ?? null,
        avgDiffN: avgDiffN[i] ?? 0,
      };
    });
  }, [displayEngineData, getConceptDisplayName, mathConceptsMap]);

  // 정렬된 Engine 테이블 rows
  const sortedEngineTableRows = useMemo(() => {
    if (!engineTableRows.length) return [];
    if (!engineTableSort.column || !engineTableSort.direction) return engineTableRows;

    const sorted = [...engineTableRows];
    const { column, direction } = engineTableSort;
    const multiplier = direction === 'asc' ? 1 : -1;

    sorted.sort((a, b) => {
      let aVal, bVal;
      switch (column) {
        case 'cid':
          aVal = a.cid;
          bVal = b.cid;
          break;
        case 'r':
          aVal = a.r ?? -Infinity;
          bVal = b.r ?? -Infinity;
          break;
        case 'h':
          aVal = a.h ?? -Infinity;
          bVal = b.h ?? -Infinity;
          break;
        case 'st':
          aVal = a.st ?? -Infinity;
          bVal = b.st ?? -Infinity;
          break;
        case 'dr':
          aVal = a.dr ?? -Infinity;
          bVal = b.dr ?? -Infinity;
          break;
        case 'dh':
          aVal = a.dh ?? -Infinity;
          bVal = b.dh ?? -Infinity;
          break;
        case 'ds':
          aVal = a.ds ?? -Infinity;
          bVal = b.ds ?? -Infinity;
          break;
        case 'lastRecord':
          // 정답률 기준 (정답/총)
          aVal = a.lastRecord ? a.lastRecord[0] / (a.lastRecord[1] || 1) : -Infinity;
          bVal = b.lastRecord ? b.lastRecord[0] / (b.lastRecord[1] || 1) : -Infinity;
          break;
        case 'totalRecord':
          // 정답률 기준 (정답/총)
          aVal = a.totalRecord ? a.totalRecord[0] / (a.totalRecord[1] || 1) : -Infinity;
          bVal = b.totalRecord ? b.totalRecord[0] / (b.totalRecord[1] || 1) : -Infinity;
          break;
        case 'avgDiff':
          aVal = a.avgDiff ?? -Infinity;
          bVal = b.avgDiff ?? -Infinity;
          break;
        default:
          return 0;
      }
      if (aVal < bVal) return -1 * multiplier;
      if (aVal > bVal) return 1 * multiplier;
      return 0;
    });

    return sorted;
  }, [engineTableRows, engineTableSort]);

  // Engine 테이블 헤더 클릭 핸들러 (내림차순 → 오름차순 → 기본 순환)
  const handleEngineTableSort = useCallback((column) => {
    setEngineTableSort((prev) => {
      if (prev.column !== column) {
        // 새 컬럼 선택: 내림차순부터 시작
        return { column, direction: 'desc' };
      }
      // 같은 컬럼: desc → asc → null 순환
      if (prev.direction === 'desc') {
        return { column, direction: 'asc' };
      }
      if (prev.direction === 'asc') {
        return { column: null, direction: null };
      }
      return { column, direction: 'desc' };
    });
  }, []);

  // 개념명에서 짧은 라벨 추출 (콜론 뒤 부분만)
  const getConceptShortName = useCallback((cid) => {
    const concept = mathConceptsMap.get(String(cid));
    if (!concept?.text) return String(concept?.order ?? cid);
    // "AL-DEF:집합" → "집합"
    const parts = concept.text.split(':');
    return parts.length > 1 ? parts[1] : parts[0];
  }, [mathConceptsMap]);

  // 표시 필터용 — 지금 화면에 그려지는 시점 기준(재생 중이면 그 프레임). 개념은 '학습이 처음 반영된
  // 프레임'에 등장하고, 그 전 프레임에선 원점 더미로 쌓이지 않는다.
  const defaultCidSet = useMemo(() => collectDefaultCids(displayEngineData), [displayEngineData]);

  // ScatterChart용 데이터 변환 (type 정보 포함)
  const scatterDataAll = useMemo(() => {
    return engineTableRows
      .filter(row => row.r !== null && row.h !== null)
      .map(row => {
        const concept = mathConceptsMap.get(String(row.cid));
        const conceptType = extractConceptType(concept);
        const mathType = concept?.math_type || null;
        return {
          // 기본값
          x: row.r,                           // -1 ~ 1 범위
          y: row.h,                           // -1 ~ 1 범위
          z: row.st ?? 0,                     // stability (점 크기 + 색상)
          // delta 값
          dx: row.dr,                         // delta r
          dy: row.dh,                         // delta h
          dz: row.ds ?? 0,                    // delta stability
          cid: row.cid,
          name: row.nameRendered,             // 툴팁용 (HTML)
          label: getConceptShortName(row.cid), // 라벨용 (짧은 텍스트)
          conceptType,
          mathType,
          tags: concept?.tags || [],
          order: concept?.order ?? null
        };
      });
  }, [engineTableRows, getConceptShortName, mathConceptsMap, extractConceptType]);

  // 이후 모든 파생값(개수 분모·태그 목록·r/h/st·안정도 범위·점 크기 스케일)의 기준.
  // 여기서 제외해야 슬라이더 눈금과 축이 '실제로 움직인 개념'의 분포로 다시 계산된다.
  const scatterDataRaw = useMemo(() => {
    if (!hideDefaults || defaultCidSet.size === 0) return scatterDataAll;
    return scatterDataAll.filter(d => !defaultCidSet.has(d.cid));
  }, [scatterDataAll, hideDefaults, defaultCidSet]);

  // 공통 정렬 함수 (column이 null이면 원본 순서 = concept_index 순)
  const sortConceptRows = (rows, sort) => {
    if (!sort.column) return rows;
    const valMap = { r: 'x', h: 'y', st: 'z', order: 'order' };
    const key = valMap[sort.column];
    return [...rows].sort((a, b) => {
      return ((b[key] ?? -Infinity) - (a[key] ?? -Infinity)) * (sort.direction === 'desc' ? 1 : -1);
    });
  };

  const handleTableSortClick = (column, currentSort, setSortFn) => {
    if (currentSort.column === column) {
      if (currentSort.direction === 'asc') {
        setSortFn({ column, direction: 'desc' });
      } else {
        setSortFn({ column: null, direction: null });
      }
    } else {
      setSortFn({ column, direction: 'asc' });
    }
  };

  // ScatterChart 데이터 필터링
  // 수학개념 첫 번째 태그 목록(분류 필터 옵션) — 초기값 숨김과 무관한 전체(scatterDataAll) 기준.
  // scatterDataRaw 기준으로 두면 숨김·프레임 전환으로 옵션이 사라지면서 아래 effect 가 사용자가 걸어둔
  // selectedTag 를 조용히 해제해 버린다(재생 종료 후에도 복원되지 않음).
  // (scatterDataAll 자체는 여전히 displayEngineData 파생이지만 concept_index 는 프레임 불변이라 옵션은 고정된다.)
  const allFirstTags = useMemo(() => {
    const set = new Set();
    scatterDataAll.forEach((d) => {
      const t = Array.isArray(d.tags) ? d.tags[0] : null;
      if (t) set.add(t);
    });
    return Array.from(set).sort((a, b) => String(a).localeCompare(String(b), 'ko'));
  }, [scatterDataAll]);

  // 데이터 변경으로 선택 태그가 사라지면 해제
  useEffect(() => {
    if (selectedTag && !allFirstTags.includes(selectedTag)) setSelectedTag(null);
  }, [allFirstTags, selectedTag]);

  const scatterData = useMemo(() => {
    return scatterDataRaw
      .filter(item => {
        // Delta 모드일 때 delta 값이 있는 것만
        if (showDelta && (item.dx === null || item.dy === null)) {
          return false;
        }
        // Type 필터
        if (selectedTypes.length > 0 && (!item.conceptType || !selectedTypes.includes(item.conceptType))) {
          return false;
        }
        if (selectedMathTypes.length > 0 && (!item.mathType || !selectedMathTypes.includes(item.mathType))) {
          return false;
        }
        // 태그 필터 (첫 번째 태그 기준 분류)
        if (selectedTag && (!Array.isArray(item.tags) || item.tags[0] !== selectedTag)) {
          return false;
        }
        // 범위 필터(안정도·r/h/st)는 재생 중엔 우회 — 전 프레임 점을 다 보여주고 축은 union으로 동결한다.
        // (범위 state는 그대로 두고 종료 시 복원 → 비단조 궤적의 점이 라이브 범위 밖이라 사라지는 문제 방지)
        if (playbackIdx == null) {
          const stabilityValue = showDelta ? (item.dz ?? 0) : (item.z ?? 0);
          if (stabilityValue < stabilityRange[0] || stabilityValue > stabilityRange[1]) {
            return false;
          }
          const rVal = showDelta ? item.dx : item.x;
          const hVal = showDelta ? item.dy : item.y;
          const stVal = showDelta ? (item.dz ?? 0) : (item.z ?? 0);
          if (rVal != null && (rVal < rRange[0] || rVal > rRange[1])) return false;
          if (hVal != null && (hVal < hRange[0] || hVal > hRange[1])) return false;
          if (stVal < stRange[0] || stVal > stRange[1]) return false;
        }
        return true;
      })
      .map(item => ({
        ...item,
        // 차트에 사용할 실제 값 (delta 모드 여부에 따라)
        plotX: showDelta ? item.dx : item.x,
        plotY: useStabilityY
          ? (showDelta ? (item.dz ?? 0) : (item.z ?? 0))
          : (showDelta ? item.dy : item.y),
        plotZ: showDelta ? (item.dz ?? 0) : (item.z ?? 0)
      }));
  }, [scatterDataRaw, selectedTypes, selectedMathTypes, selectedTag, stabilityRange, rRange, hRange, stRange, showDelta, useStabilityY, playbackIdx]);

  // r/h/st 범위 필터용 bounds (필터 적용 전 scatterDataRaw 기준)
  const rhstBounds = useMemo(() => {
    if (scatterDataRaw.length === 0) return { r: [-1, 1], h: [-1, 1], st: [0, 1] };
    const rVals = scatterDataRaw.map(d => showDelta ? d.dx : d.x).filter(v => v != null);
    const hVals = scatterDataRaw.map(d => showDelta ? d.dy : d.y).filter(v => v != null);
    const stVals = scatterDataRaw.map(d => showDelta ? (d.dz ?? 0) : (d.z ?? 0));
    const bounds = (arr) => arr.length === 0 ? [-1, 1] : [Math.min(...arr), Math.max(...arr)];
    return { r: bounds(rVals), h: bounds(hVals), st: bounds(stVals) };
  }, [scatterDataRaw, showDelta]);

  // delta 모드 전환 또는 최초 데이터 로드 시 범위 필터 리셋
  // 디폴트 숨김 토글도 리셋 대상 — 숨김을 끄면 bounds 가 넓어지는데 옛 range 를 그대로 두면
  // 새로 드러난 점이 곧바로 범위 필터에 걸려 사라지는 dead-end 가 된다.
  // 판정(재생 중 skip 포함)은 shouldResetRanges — false 면 prev 상태를 갱신하지 않으므로 재생 종료 후
  // 라이브 bounds 로 정확히 1회 재적합된다(재생 중 rhstBounds 는 '그 프레임' 분포라 그대로 쓰면 안 된다).
  const prevShowDeltaRef = useRef(showDelta);
  const prevHideDefaultsRef = useRef(hideDefaults);
  const prevBoundsRef = useRef(null);
  useEffect(() => {
    const reset = shouldResetRanges({
      inPlayback: playbackIdx != null,
      prevShowDelta: prevShowDeltaRef.current,
      showDelta,
      prevHideDefaults: prevHideDefaultsRef.current,
      hideDefaults,
      hasBounds: prevBoundsRef.current !== null,
      rawLength: scatterDataRaw.length,
    });
    if (!reset) return;

    prevShowDeltaRef.current = showDelta;
    prevHideDefaultsRef.current = hideDefaults;
    prevBoundsRef.current = rhstBounds;
    setRRange(rhstBounds.r);
    setHRange(rhstBounds.h);
    setStRange(rhstBounds.st);
  }, [showDelta, hideDefaults, rhstBounds, scatterDataRaw.length, playbackIdx]);

  // 컬럼 필터 팝오버 열기
  const openColumnFilter = useCallback((column, anchorEl, sortState, setSortFn) => {
    const rect = anchorEl.getBoundingClientRect();
    setColumnFilterPopup(prev =>
      prev && prev.column === column ? null : { column, rect, sortState, setSortFn }
    );
  }, []);

  // 범위 필터가 기본값(bounds)과 다른지 확인
  const isRangeFiltered = useCallback((column) => {
    const bounds = rhstBounds[column];
    const range = column === 'r' ? rRange : column === 'h' ? hRange : stRange;
    return range[0] > bounds[0] || range[1] < bounds[1];
  }, [rhstBounds, rRange, hRange, stRange]);

  // 테이블용 정렬 데이터 (필터 적용된 scatterData 기반)
  const rSortedRows = useMemo(() => (currentTab !== 'ai' || aiViewMode !== 'table') ? [] : sortConceptRows(scatterData, rTableSort), [currentTab, aiViewMode, scatterData, rTableSort]);
  const hSortedRows = useMemo(() => (currentTab !== 'ai' || aiViewMode !== 'table') ? [] : sortConceptRows(scatterData, hTableSort), [currentTab, aiViewMode, scatterData, hTableSort]);
  const stSortedRows = useMemo(() => (currentTab !== 'ai' || aiViewMode !== 'table') ? [] : sortConceptRows(scatterData, stTableSort), [currentTab, aiViewMode, scatterData, stTableSort]);
  const chartSideSortedRows = useMemo(() => (currentTab !== 'ai' || aiViewMode !== 'chart') ? [] : sortConceptRows(scatterData, chartSideTableSort), [currentTab, aiViewMode, scatterData, chartSideTableSort]);
  // 차트 옆 테이블 하이라이트 대상: 부모 선택(selectedConceptCids) + 내부 dot 선택(selectedPoint)
  const chartSideSelectedCids = useMemo(
    () => (selectedPoint != null ? [...selectedConceptCids, selectedPoint] : selectedConceptCids),
    [selectedConceptCids, selectedPoint]
  );
  // 차트 옆 테이블에 실제로 렌더되는 행('선택만' 적용 후) — 0행 사유 안내가 같은 기준을 쓰도록 분리
  const chartSideVisibleRows = useMemo(
    () => (showSelectedOnly ? chartSideSortedRows.filter(r => chartSideSelectedCids.includes(r.cid)) : chartSideSortedRows),
    [showSelectedOnly, chartSideSortedRows, chartSideSelectedCids]
  );

  // 선택된 개념이 하나도 없으면 '선택만' 필터를 자동 해제(빈 목록에 갇히는 dead-end 방지)
  useEffect(() => {
    if (showSelectedOnly && chartSideSelectedCids.length === 0) setShowSelectedOnly(false);
  }, [showSelectedOnly, chartSideSelectedCids.length]);

  // 초기값 숨김으로 선택한 점이 사라지면 dot 선택도 해제 — 보이지 않는 개념이 툴팁·추이 모달
  // (cids=chartSideSelectedCids) 대상으로 남는 유령 선택 방지. 재생 중엔 프레임마다 등장/퇴장하므로 유지.
  // 판정 기준은 scatterData(범위 필터 적용 후)가 아니라 scatterDataRaw다 — 범위 필터의 비교 기준(rRange 등)은
  // Δ 토글 시 한 커밋 늦게 리셋되므로, 그 창에서 scatterData가 일시적으로 비어 선택이 날아간다.
  // scatterDataRaw는 delta/절대 모드와 무관해 그 오발화가 원리적으로 불가능하다.
  useEffect(() => {
    if (playbackIdx != null || selectedPoint == null) return;
    if (!scatterDataRaw.some(p => p.cid === selectedPoint)) setSelectedPoint(null);
  }, [scatterDataRaw, selectedPoint, playbackIdx]);

  // 차트 크기 상태 (겹침 계산용)
  const [chartDimensions, setChartDimensions] = useState({ width: 0, height: 0 });

  // 축 범위: 데이터의 최소/최대값 사용. 재생 중엔 전 프레임 union 한계(frozenBounds)로 데이터 한계를
  // 동결 → 프레임마다 축이 리스케일되어 '점의 이동'과 '축 확대'가 뒤섞이는 것을 막는다.
  // (여백 gutter 확장은 현재 chartDimensions로 계속 반응형 — 리사이즈 시에도 점이 잘리지 않게)
  const axisRange = useMemo(() => {
    let rawXLim; let rawYLim;
    if (playbackIdx != null && frozenBounds) {
      rawXLim = frozenBounds.rawXLim;
      rawYLim = frozenBounds.rawYLim;
    } else {
      if (scatterData.length === 0) return { xMin: -1, xMax: 1, yMin: -1, yMax: 1 };
      const xValues = scatterData.map(item => item.plotX).filter(v => v !== null && v !== undefined);
      const yValues = scatterData.map(item => item.plotY).filter(v => v !== null && v !== undefined);
      if (xValues.length === 0 || yValues.length === 0) return { xMin: -1, xMax: 1, yMin: -1, yMax: 1 };
      // 원점(0,0)이 항상 정중앙에 오도록 x·y 도메인을 0 기준 대칭으로 설정
      rawXLim = Math.max(
        Math.abs(Math.floor(Math.min(...xValues) * 10) / 10),
        Math.abs(Math.ceil(Math.max(...xValues) * 10) / 10),
        0.1
      );
      rawYLim = Math.max(
        Math.abs(Math.floor(Math.min(...yValues) * 10) / 10),
        Math.abs(Math.ceil(Math.max(...yValues) * 10) / 10),
        0.1
      );
    }
    // 점 배지가 외곽에서 잘리지 않도록 사방 동일(px) 여백만큼 도메인을 확장
    const GUTTER = 24;
    const pw = chartDimensions.width - 20;   // plot 폭 (margin 10*2)
    const ph = chartDimensions.height - 20;  // plot 높이
    const xLim = pw > GUTTER * 2 ? rawXLim / (1 - (2 * GUTTER) / pw) : rawXLim;
    const yLim = ph > GUTTER * 2 ? rawYLim / (1 - (2 * GUTTER) / ph) : rawYLim;
    return { xMin: -xLim, xMax: xLim, yMin: -yLim, yMax: yLim };
  }, [scatterData, chartDimensions, playbackIdx, frozenBounds]);

  // r·h 수치 눈금 값 (0.1 간격, 원점·최외곽 여백 제외)
  const gridLines = useMemo(() => {
    const step = 0.1;
    const xs = [], ys = [];
    for (let v = step; v < axisRange.xMax - 0.05; v += step) { xs.push(Number(v.toFixed(1)), Number((-v).toFixed(1))); }
    for (let v = step; v < axisRange.yMax - 0.05; v += step) { ys.push(Number(v.toFixed(1)), Number((-v).toFixed(1))); }
    return { xs, ys };
  }, [axisRange]);

  // Stability/Delta st 범위 계산 (슬라이더용). 재생 중엔 union 동결 → 슬라이더 눈금이 프레임마다 튀지 않음.
  const stabilityBounds = useMemo(() => {
    if (playbackIdx != null && frozenBounds) return frozenBounds.stabilityBounds;
    if (scatterDataRaw.length === 0) return { min: 0, max: 1 };
    const values = scatterDataRaw
      .map(item => showDelta ? (item.dz ?? 0) : (item.z ?? 0))
      .filter(v => v !== null && v !== undefined);
    if (values.length === 0) return { min: 0, max: 1 };
    const min = Math.floor(Math.min(...values) * 100) / 100;
    const max = Math.ceil(Math.max(...values) * 100) / 100;
    return { min, max };
  }, [scatterDataRaw, showDelta, playbackIdx, frozenBounds]);

  // Delta 모드 전환 시 슬라이더 범위 초기화 (useLayoutEffect로 깜박임 방지).
  // 재생 중엔 skip — 프레임 union 동결값이 사용자 범위를 덮어쓰지 않도록(범위 필터는 재생 중 우회됨).
  useLayoutEffect(() => {
    if (playbackIdx != null) return;
    setStabilityRange([stabilityBounds.min, stabilityBounds.max]);
  }, [showDelta, stabilityBounds.min, stabilityBounds.max, playbackIdx]);

  // 상대적 점 크기 범위 계산 (필터링된 데이터 기준). 재생 중엔 union 동결 → dot 반지름이 프레임마다 재정규화되지 않음.
  const sizeRange = useMemo(() => {
    if (playbackIdx != null && frozenBounds) return frozenBounds.sizeRange;
    if (scatterData.length === 0) return { min: 0, max: 1 };
    const values = scatterData.map(d => d.plotZ).filter(v => v !== null && v !== undefined);
    if (values.length === 0) return { min: 0, max: 1 };
    return {
      min: Math.min(...values),
      max: Math.max(...values)
    };
  }, [scatterData, playbackIdx, frozenBounds]);

  // 상대적 반지름 계산 함수 (DOT_MIN_RADIUS ~ DOT_MAX_RADIUS). 순수 relativeRadius 래퍼.
  const getRelativeRadius = useCallback((value) => {
    return relativeRadius(value, sizeRange, DOT_MIN_RADIUS, DOT_MAX_RADIUS);
  }, [sizeRange]);

  const handleChartResize = useCallback((width, height) => {
    if (resizeTimerRef.current) clearTimeout(resizeTimerRef.current);
    resizeTimerRef.current = setTimeout(() => {
      setChartDimensions({ width, height });
    }, 150);
  }, []);

  // 화면 좌표 계산 함수. 순수 projectPoint 래퍼(마진 10 고정, dims 미확정 시 null).
  const getScreenCoords = useCallback((item) => {
    return projectPoint(item, axisRange, chartDimensions);
  }, [chartDimensions, axisRange]);

  // 점 좌표 계산 (클러스터링 없이 전부 개별 렌더)
  const clusteredData = useMemo(() => {
    if (currentTab !== 'ai' || aiViewMode !== 'chart') return { singles: [], clusters: [] };
    if (scatterData.length === 0 || !chartDimensions.width) {
      return { singles: scatterData, clusters: [] };
    }

    // 각 점의 화면 좌표 + 반지름 계산
    const pointsWithCoords = scatterData.map(item => {
      const coords = getScreenCoords(item);
      const radius = getRelativeRadius(item.plotZ);
      return { ...item, screenX: coords?.x ?? 0, screenY: coords?.y ?? 0, radius };
    });

    // 클러스터링 제거: 모든 점을 개별(single)로 렌더
    return { singles: pointsWithCoords, clusters: [] };
  }, [currentTab, aiViewMode, scatterData, chartDimensions, getScreenCoords, getRelativeRadius]);

  // 반원 색쌍별 gradient(<defs>용). 색은 프레임 불변이라 재생 중 재계산돼도 동일 결과.
  const dotGradients = useMemo(() => {
    const m = new Map();
    for (const p of clusteredData.singles) {
      const id = dotGradId(getMathTypeColor(p.mathType), getConceptTypeColor(p.conceptType));
      if (!m.has(id)) m.set(id, { left: splitRgba(getMathTypeColor(p.mathType)), right: splitRgba(getConceptTypeColor(p.conceptType)) });
    }
    return m;
  }, [clusteredData]);

  // 점 이동 활공: 타임라인 중이면 기본 ON — 자동재생 틱(+1)·스크럽(모든 이동량, 역방향 포함) 모두 활공.
  // '좌표계가 바뀌는' 커밋에서만 1커밋 하드컷(즉시 스냅): 라이브→프레임 진입, Δ/Y:st 축전환,
  // frozenBounds 재계산, 리사이즈, 명시 스냅(되감기 재개=animSnapOnceRef). idx가 안 바뀐 무관 리렌더
  // (점 선택 등)엔 하드컷하지 않아 진행 중 전이가 취소되지 않는다(직전 커밋값과 비교해 판정).
  const modeChanged = animPrevDeltaRef.current !== showDelta || animPrevStabYRef.current !== useStabilityY;
  const dimsChanged = animPrevDimsRef.current !== chartDimensions;
  const boundsChanged = animPrevBoundsRef.current !== frozenBounds;
  const enteringFromLive = animPrevIdxRef.current == null && playbackIdx != null;
  const hardCut = modeChanged || dimsChanged || boundsChanged || enteringFromLive || animSnapOnceRef.current;
  const slideOn = playbackIdx != null && !reducedMotion && !hardCut;
  // 커밋 후 '직전 값' 갱신 + 1회성 스냅 플래그 소거 → 다음 렌더는 이 커밋값과 비교, 명시 스냅은 1커밋만.
  useEffect(() => {
    animPrevIdxRef.current = playbackIdx;
    animPrevDeltaRef.current = showDelta;
    animPrevStabYRef.current = useStabilityY;
    animPrevDimsRef.current = chartDimensions;
    animPrevBoundsRef.current = frozenBounds;
    animSnapOnceRef.current = false;
  });

  // ── 일별 재생 제어 ──────────────────────────────────────────────
  // 재생 가능 날짜 = geneticsData 시험 updated_at(KST) ∩ 현재 필터[startDate, endDate]. 오름차순·중복제거.
  const availableTimelineDates = useMemo(() => {
    if (!geneticsData || !Array.isArray(geneticsData.stamps)) return [];
    const set = new Set();
    geneticsData.stamps.forEach((st) => {
      (st.exams || []).forEach((ex) => {
        const d = kstDateOf(ex.updated_at);
        if (d && (!startDate || d >= startDate) && (!endDate || d <= endDate)) set.add(d);
      });
    });
    return Array.from(set).sort();
  }, [geneticsData, startDate, endDate]);

  const stopPlayback = useCallback(() => {
    playbackGenRef.current += 1; // in-flight 프리로드 폐기
    if (playIntervalRef.current) { clearInterval(playIntervalRef.current); playIntervalRef.current = null; }
    setIsPlaying(false);
    setPlaybackPreloading(false);
    setPlaybackIdx(null);
    setFrozenBounds(null);
  }, []);

  // 프레임 프리로드(재생·추이 공용): envelope 생성/로드 → 빠진 날이 있으면 타임라인 조회 한 번으로 전 날짜 채움 → 저장.
  // 타임라인 조회가 안 되면(그 경로가 없는 옛 서버 포함) 날짜별 /engine 차례 호출(옛 방식)로 넘어간다.
  // shouldAbort(): true면 즉시 폐기(호출자별 세대/취소 토큰). onProgress(loaded,total): 진행 통지(선택).
  // 반환: 완성 envelope(모든 프레임 보유) 또는 null(중단·실패·날짜<2). envelopeRef/framesRef도 갱신(재생 표시가 참조).
  const ensureFramesLoaded = useCallback(async (shouldAbort, onProgress) => {
    if (!studentId || !engineData) return null;
    const dates = availableTimelineDates;
    if (dates.length < 2) return null; // 프레임 1개 이하면 시계열 무의미

    const fp = fingerprintOf(engineData);
    const key = timelineKey(studentId, startDate, endDate);
    let env = loadEnvelope(key);
    if (!env || env.fingerprint !== fp) {
      env = { v: TIMELINE_VERSION, fingerprint: fp, conceptIndex: engineData.concept_index, vScale: engineData.v_scale || 10000, dates, frames: {} };
    } else {
      env.dates = dates; // 동일 지문·필터면 동일하나 안전하게 현재 목록으로(부분 저장 재개 지원)
    }
    envelopeRef.current = env;
    framesRef.current = env.frames;

    let missing = dates.filter((d) => !env.frames[d]);
    if (onProgress) onProgress(dates.length - missing.length, dates.length);

    if (missing.length > 0) {
      // 라이브와 같은 기간으로 불러야 응답의 지문 세 값이 라이브 /engine 과 같은 뜻이 된다.
      // 지문은 프레임을 실제로 만든 응답의 것으로 바꿔 둔다 — 서버 워커마다 K 캐시가 따로라 k.seq 가 라이브와
      // 잠깐 다를 수 있고(회신 §3 끝), 그러면 다음 호출에서 이 캐시를 버리고 다시 받는다.
      const tl = await fetchEngineTimeline(studentId, startDate, endDate);
      if (shouldAbort()) return null;
      const frames = tl.data ? framesForDates(tl.data, dates) : null;
      if (frames) {
        // 제자리에 채운다(객체를 갈아끼우지 않는다) — envelopeRef·framesRef 는 진입 때 이 env 한 쌍을 가리키므로,
        // 재생과 추이 창이 동시에 부르고 응답이 거꾸로 와도 개념 순서(conceptIndex)와 값 배열이 서로 다른 응답에서 오지 않는다.
        Object.assign(env.frames, frames);
        env.fingerprint = timelineFingerprintOf(tl.data);
        env.conceptIndex = tl.data.concept_index; // 프레임 배열과 짝 — 같은 응답의 것
        env.vScale = tl.data.v_scale || 10000;
        missing = [];
        if (onProgress) onProgress(dates.length, dates.length);
      }
    }

    if (missing.length > 0) {
      // ── 옛 방식: 타임라인 조회가 안 됐을 때만 ──
      // 마지막 프레임 재사용(fetch 1회 절약)은 라이브 engineData의 마지막 시험이 실제로 lastDate일 때만.
      // 트리 필터로 엔진이 lastDate(=geneticsData 기준) 이후 시험을 알 수 있어(engine ⊇ students), 아니면
      // 라이브가 overshoot → 그 경우엔 일반 프레임처럼 end_date=lastDate로 정확히 fetch.
      const lastDate = dates[dates.length - 1];
      const liveLastDate = kstDateOf(engineData.delta?.exam?.updated_at);
      if (!env.frames[lastDate] && liveLastDate === lastDate) env.frames[lastDate] = extractFrame(engineData);
      missing = dates.filter((d) => !env.frames[d]);

      // 순차 프리로드(서버 부하 완화). 실패 프레임은 1회 재시도 후 실패 시 취소(스킵은 시간축 왜곡).
      for (let i = 0; i < missing.length; i += 1) {
        if (shouldAbort()) return null; // 학생/필터/탭 변경·모달 닫힘 등 → 폐기
        const date = missing[i];
        let res = await fetchEngineFrame(studentId, startDate, date);
        if (shouldAbort()) return null; // 첫 fetch 사이 중단 시 재시도 낭비 방지
        if (res.error || !res.data) res = await fetchEngineFrame(studentId, startDate, date);
        if (shouldAbort()) return null;
        if (res.error || !res.data) return null;
        env.frames[date] = extractFrame(res.data);
        if (onProgress) onProgress(dates.length - missing.length + i + 1, dates.length);
      }
    }
    if (shouldAbort()) return null;

    saveEnvelope(key, env); // 완료 시 1회 저장(중단 시엔 미저장 — 다음 호출이 부분 재개)
    return env;
  }, [studentId, engineData, availableTimelineDates, startDate, endDate, fetchEngineFrame, fetchEngineTimeline]);

  const startPlayback = useCallback(async () => {
    if (isPlaying || playbackPreloading) return;
    if (!studentId || !engineData) return;
    const dates = availableTimelineDates;
    if (dates.length < 2) return; // 프레임 1개 이하면 재생 무의미
    const gen = playbackGenRef.current + 1;
    playbackGenRef.current = gen;
    setTimelineDates(dates);

    setPlaybackProgress({ loaded: 0, total: dates.length });
    setPlaybackPreloading(true);
    const env = await ensureFramesLoaded(
      () => playbackGenRef.current !== gen,
      (loaded, total) => setPlaybackProgress({ loaded, total }),
    );
    if (playbackGenRef.current !== gen) return; // 이 재생이 폐기됨 — 후속 상태는 새 세대가 관리
    if (!env) { setPlaybackPreloading(false); return; } // 프리로드 실패

    setPlaybackPreloading(false);
    setFrozenBounds(computeFrozenBounds(env, showDelta, useStabilityY, hideDefaults));
    setPlaybackIdx(0);
    setIsPlaying(true);
  }, [isPlaying, playbackPreloading, studentId, engineData, availableTimelineDates, showDelta, useStabilityY, hideDefaults, ensureFramesLoaded]);

  // 재생 틱: 700ms마다 다음 프레임(마지막에서 정지 대기)
  useEffect(() => {
    if (!isPlaying) return undefined;
    playIntervalRef.current = setInterval(() => {
      setPlaybackIdx((i) => (i == null || i + 1 >= timelineDates.length ? i : i + 1));
    }, PLAYBACK_TICK_MS);
    return () => { if (playIntervalRef.current) { clearInterval(playIntervalRef.current); playIntervalRef.current = null; } };
  }, [isPlaying, timelineDates.length]);

  // 마지막 프레임 도달 → 잠깐 보여준 뒤 일시정지(타임라인 유지 — 스크러버로 되감기 가능). 라이브 복귀는 '종료'.
  useEffect(() => {
    if (isPlaying && playbackIdx != null && timelineDates.length > 0 && playbackIdx >= timelineDates.length - 1) {
      const t = setTimeout(() => setIsPlaying(false), 900);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [isPlaying, playbackIdx, timelineDates.length]);

  // 스크러버 전송 제어
  const togglePlayback = useCallback(() => {
    if (isPlaying) { setIsPlaying(false); return; } // 재생 중 → 일시정지
    // 정지 상태에서 재생: 마지막 프레임이면 처음부터(되감기는 활공 없이 즉시 스냅 — 스크럽과 구분)
    if (playbackIdx != null && playbackIdx >= timelineDates.length - 1) {
      animSnapOnceRef.current = true;
      setPlaybackIdx(0);
    }
    setIsPlaying(true);
  }, [isPlaying, playbackIdx, timelineDates.length]);

  const scrubTo = useCallback((idx) => {
    setIsPlaying(false);       // 수동 스크럽 → 자동재생 일시정지
    setPlaybackIdx(idx);
  }, []);

  // 타임라인 모드(재생/일시정지 무관)에서 Δ/Y:st·디폴트 숨김 토글 시 동결 bounds 재계산. inTimeline(불리언)을
  // dep로 써서 틱마다(playbackIdx 변경) 재계산되지 않으면서도, 일시정지 중 모드 토글에도 축이 갱신되게 한다.
  const inTimeline = playbackIdx != null;
  useEffect(() => {
    if (!inTimeline) return;
    setFrozenBounds(computeFrozenBounds(envelopeRef.current, showDelta, useStabilityY, hideDefaults));
  }, [showDelta, useStabilityY, hideDefaults, inTimeline]);

  // 학생/탭/뷰모드/날짜필터가 실제로 바뀌면 재생 중단(deps 변경 = 실제 값 변경 → no-op 슬라이더 클릭엔 미발동)
  useEffect(() => {
    stopPlayback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [studentId, currentTab, aiViewMode, startDate, endDate]);

  // 언마운트 시 in-flight 폐기 + 틱 정리(setState-after-unmount 방지)
  useEffect(() => () => {
    playbackGenRef.current += 1;
    if (playIntervalRef.current) clearInterval(playIntervalRef.current);
  }, []);

  // prefers-reduced-motion 추적(초기값 + 변경). true면 활공을 끄고 즉시 스텝.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const onChange = (e) => setReducedMotion(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  // (dev 전용) 백엔드 토큰 없이 점 이동 애니메이션을 검증하는 seed seam.
  // AI 탭·차트 뷰·학생(engineData) 로드 상태에서 콘솔에 window.__seedTimelineDemo() 실행 → 합성 재생.
  useEffect(() => {
    if (process.env.NODE_ENV === 'production') return undefined;
    const w = window;
    w.__seedTimeline = (dates, framesByDate, opts = {}) => {
      const env = {
        v: TIMELINE_VERSION, fingerprint: 'seed',
        conceptIndex: opts.conceptIndex || engineData?.concept_index || [],
        vScale: opts.vScale || engineData?.v_scale || 10000,
        dates, frames: framesByDate,
      };
      envelopeRef.current = env;
      framesRef.current = framesByDate;
      setTimelineDates(dates);
      setFrozenBounds(computeFrozenBounds(env, showDelta, useStabilityY, hideDefaults));
      setPlaybackIdx(0);
      setIsPlaying(true);
    };
    // 현재 engineData를 프레임0으로, 이후 좌표가 소폭 드리프트하는 nDays 합성 프레임 생성 후 재생.
    w.__seedTimelineDemo = (nDays = 8) => {
      if (currentTab !== 'ai' || aiViewMode !== 'chart') { console.warn('[seedDemo] AI 탭·차트 뷰에서 실행하세요'); return; }
      if (!engineData?.concept_index) { console.warn('[seedDemo] engineData 없음 — 학생/엔진 로드 후 실행'); return; }
      const vScale = engineData.v_scale || 10000;
      const clampV = (v, lo, hi) => Math.max(lo, Math.min(hi, v));
      const jit = (amp) => Math.round((Math.random() * 2 - 1) * amp);
      let r = (engineData.r_status || []).slice();
      let h = (engineData.h_status || []).slice();
      let s = (engineData.stability || []).slice();
      // 비null 개념의 ~12%는 특정 프레임부터 '등장'(그 전엔 null) → enter(팝인) 경로도 재현.
      const reveal = r.map((v) => (v != null && Math.random() < 0.12 ? 1 + Math.floor(Math.random() * (nDays - 1)) : 0));
      const dates = []; const frames = {};
      for (let d = 0; d < nDays; d += 1) {
        const date = `2026-03-${String(d + 1).padStart(2, '0')}`;
        dates.push(date);
        frames[date] = {
          r: r.map((v, i) => (d < reveal[i] ? null : v)),
          h: h.map((v, i) => (d < reveal[i] ? null : v)),
          s: s.slice(),
        };
        r = r.map((v) => (v == null ? v : clampV(v + jit(900), -vScale, vScale)));
        h = h.map((v) => (v == null ? v : clampV(v + jit(900), -vScale, vScale)));
        s = s.map((v) => (v == null ? v : clampV(v + jit(500), 0, vScale)));
      }
      w.__seedTimeline(dates, frames);
      console.log(`[seedDemo] ${nDays}일 합성 재생 시작 (${dates[0]}~${dates[dates.length - 1]}) — 일부 개념 중간 등장(enter) 포함`);
    };
    return () => { try { delete w.__seedTimeline; delete w.__seedTimelineDemo; } catch (_) { /* noop */ } };
  }, [engineData, showDelta, useStabilityY, hideDefaults, currentTab, aiViewMode]);

  const playbackDisabled = !engineData || isLoadingEngine || isLoadingGenetics || availableTimelineDates.length < 2;

  // 초기 디폴트(미학습) 개념 숨김 토글 — 테이블 뷰도 scatterData를 쓰므로 두 뷰 모두에 렌더한다.
  // 차트 뷰에선 재생 버튼 앞, 테이블 뷰에선 우측 정렬 후 첫 항목으로 같은 자리에 놓인다.
  // 라벨 옆 개수 = 실제로 제외된 개념 수. 기본 ON이므로 이 표시가 없으면 '개념이 원래 이만큼'으로 오해된다.
  // defaultCidSet.size 가 아니라 두 배열의 차이를 쓴다 — r/h 결측 행은 애초에 산점도 대상이 아니라 개수가 다르다.
  const hiddenDefaultCount = scatterDataAll.length - scatterDataRaw.length;

  // 빈 화면·0행 사유 — 차트/테이블/재생 오버레이가 같은 문구를 쓴다(같은 화면에서 서로 다른 원인을 지목하지
  // 않도록). 로딩·엔진데이터 부재는 호출부가 따로 걸러낸다. 재생 중엔 범위 필터가 우회되므로(타입·태그는 적용)
  // 사유가 '이 시점'인지 '필터'인지가 scatterDataRaw 유무로 갈린다.
  const emptyReason = scatterDataRaw.length === 0
    ? (hiddenDefaultCount > 0
      ? (playbackIdx != null
        ? '이 시점에는 학습 기록이 반영된 개념이 없습니다 (초기값 숨김 해제 시 표시)'
        : '학습 기록이 반영된 개념 없음 (초기값 숨김 해제 시 표시)')
      : '데이터 없음')
    : '필터 조건에 맞는 개념 없음';

  const hideDefaultsToggle = (
    <label
      className="flex shrink-0 items-center gap-1.5 cursor-pointer"
      title={'엔진 초기값(직접·연관·안정도가 baseline 정수 그대로 = 학습 기록 미반영)인 개념을 숨깁니다.\n표시값이 0.00·0.50으로 보여도 미세하게 움직인 개념은 남습니다.'}
    >
      <input
        type="checkbox"
        checked={hideDefaults}
        onChange={(e) => setHideDefaults(e.target.checked)}
        className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary"
      />
      {/* 숨긴 개수는 라이브에서만 — 재생 중엔 프레임마다 값이 바뀌며 라벨 폭이 흔들려 옆 슬라이더가 매 틱 리사이즈된다 */}
      <span className="text-xs text-gray-600">
        초기값 숨김{hideDefaults && playbackIdx == null && hiddenDefaultCount > 0 ? ` ${hiddenDefaultCount}` : ''}
      </span>
    </label>
  );

  // 테이블 뷰 3표 공통 — 범위·태그 필터로 0행이 된 사유 안내(차트 뷰 빈 상태와 같은 문구).
  // 겉 게이트가 scatterDataRaw 기준이라 이게 없으면 머리글만 있는 빈 표 3개가 무설명으로 남는다.
  const emptyFilterRow = (
    <tr>
      <td colSpan={4} className="px-2 py-3 text-center text-[11px] text-gray-400">
        {emptyReason}
      </td>
    </tr>
  );

  // 단독 점 클릭 핸들러
  // 차트 옆 테이블에서 해당 행을 컨테이너 세로 가운데로 스크롤 (dot 선택 시에만 호출)
  const scrollChartTableToCid = useCallback((cid) => {
    const container = chartSideTableRef.current;
    if (!container || cid == null) return;
    const row = container.querySelector(`[data-cid="${cid}"]`);
    if (!row) return;
    const cRect = container.getBoundingClientRect();
    const rRect = row.getBoundingClientRect();
    const rowCenter = (rRect.top - cRect.top) + rRect.height / 2; // 컨테이너 상단 기준 행 중심
    const target = container.scrollTop + rowCenter - container.clientHeight / 2;
    container.scrollTo({ top: Math.max(0, target), behavior: 'smooth' });
  }, []);

  const handlePointClick = useCallback((point) => {
    scrollChartTableToCid(point.cid); // 좌표평면 dot 선택 시에만 테이블 행을 세로 가운데로 정렬
    // 임베디드 모드: 상세 팝업 대신 상위로 개념 선택을 통지
    if (onConceptSelect) {
      onConceptSelect(point.cid, point.name || point.label);
      return;
    }
    setSelectedPoint(prev => prev === point.cid ? null : point.cid);
  }, [onConceptSelect, scrollChartTableToCid]);

  // 좌표평면 드래그(rubber-band) 다중 선택 — onConceptsSelect가 전달된 경우에만 활성(타 소비처 무영향)
  const handleDragMouseDown = useCallback((e) => {
    if (typeof onConceptsSelect !== 'function') return; // 배치 콜백 없으면 드래그 비활성
    if (e.button !== 0 || showFilterPanel) return;      // 좌클릭만 · 필터 드로어 열림 시 억제
    const container = scatterContainerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    didDragRef.current = false; // 이전 상호작용 잔여 플래그 초기화
    dragStateRef.current = { startX: e.clientX, startY: e.clientY, rectLeft: rect.left, rectTop: rect.top, moved: false };

    const onMove = (ev) => {
      const st = dragStateRef.current;
      if (!st) return;
      if (!st.moved && Math.hypot(ev.clientX - st.startX, ev.clientY - st.startY) < 5) return; // 5px 임계로 클릭/드래그 구분
      if (!st.moved) setIsPlaying(false); // 드래그 확정 시 1회 재생 정지(단순 클릭은 재생 유지) → 히트테스트 시각 일치
      st.moved = true;
      ev.preventDefault(); // 드래그 중 텍스트 선택 억제
      setDragRect({ x0: st.startX - st.rectLeft, y0: st.startY - st.rectTop, x1: ev.clientX - st.rectLeft, y1: ev.clientY - st.rectTop });
    };
    const onUp = (ev) => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      const st = dragStateRef.current;
      dragStateRef.current = null;
      setDragRect(null);
      if (!st || !st.moved) return;   // 이동 없으면 클릭 → dot onClick(단일 토글)에 위임
      didDragRef.current = true;      // 후행 click 1회 억제(캡처페이즈)
      if (!(chartDimensions.width > 0 && chartDimensions.height > 0)) return; // 좌표계 유효 가드
      const L = Math.min(st.startX, ev.clientX) - st.rectLeft;
      const R = Math.max(st.startX, ev.clientX) - st.rectLeft;
      const T = Math.min(st.startY, ev.clientY) - st.rectTop;
      const B = Math.max(st.startY, ev.clientY) - st.rectTop;
      const seen = new Set();
      const batch = [];
      let primary = null;
      for (const p of clusteredData.singles) {
        const r = p.radius || 0; // split-dot 좌/우 반원 = 반지름 r의 원으로 근사(cid 단위)
        if (p.screenX + r < L || p.screenX - r > R || p.screenY + r < T || p.screenY - r > B) continue;
        if (seen.has(p.cid)) continue;
        seen.add(p.cid);
        batch.push({ cid: p.cid, name: p.name || p.label });
        if (!primary || p.screenY < primary.screenY) primary = p; // 화면 최상단 = 스크롤 기준
      }
      if (batch.length === 0) return;
      onConceptsSelect(batch);                 // 부모에서 add-only 병합(setState 1회)
      setShowSelectedOnly(true);               // 좌표평면 드래그로 개념 선택 시 '선택만' 필터 자동 활성화
      scrollChartTableToCid(primary.cid);      // 배치당 스크롤 1회만(smooth 중첩 방지)
    };

    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [onConceptsSelect, showFilterPanel, chartDimensions, clusteredData, scrollChartTableToCid]);

  // 드래그 직후 발생하는 click을 캡처페이즈에서 1회 소거 → dot onClick(단일 토글) 오발화 방지
  const handleContainerClickCapture = useCallback((e) => {
    if (didDragRef.current) {
      e.stopPropagation();
      didDragRef.current = false;
    }
  }, []);

  return (
    <div className="flex min-h-0 flex-1 flex-col" style={{ minHeight: '200px' }}>
      {/* 학생 정보 요약 */}
      {studentInfo && !hideStudentInfo && (
        <div className="mb-2 flex items-center gap-4 rounded-lg bg-gray-50 p-[7.2px]">
          {/* 학생 번호 — 학생관리 표와 같은 동그란 초록 번호(오너 지시 2026-09-11) */}
          <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-primary to-primary/70 text-sm font-bold text-white" title={`학생 ID: ${studentId}`}>
            {studentId}
          </div>
          <div className="flex-1">
            <p className="font-semibold text-gray-900">{studentInfo.student_name}</p>
            <p className="text-sm text-gray-500">{studentInfo.school_name} {studentInfo.school_grade}학년</p>
          </div>
          {geneticsData && (
            <div className="flex gap-4 text-sm">
              {engineData && (
                <div className="text-center">
                  <p className="text-xs text-gray-500">테스트 수</p>
                  <p className="font-bold text-gray-900">{engineData.exam_counts}</p>
                </div>
              )}
              <div className="text-center">
                <p className="text-xs text-gray-500">총 풀이</p>
                <p className="font-bold text-primary">{geneticsData.solved_problems}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-gray-500">고유 문제</p>
                <p className="font-bold text-green-600">{geneticsData.total_problems}</p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* 로딩 상태 */}
      {isLoadingGenetics ? (
        <div className="flex flex-1 items-center justify-center">
          <div className="flex flex-col items-center">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
            <p className="mt-4 text-gray-600">데이터 로딩 중...</p>
          </div>
        </div>
      ) : geneticsData ? (
        <div className="flex min-h-0 flex-1 flex-col">
          {/* 날짜 범위 선택 슬라이더 */}
          {dateRange.min && dateRange.max && (
            <div className="shrink-0">
              <DateRangeSlider
                minDate={dateRange.min}
                maxDate={dateRange.max}
                startDate={startDate}
                endDate={endDate}
                onStartDateChange={handleStartDateChange}
                onEndDateChange={handleEndDateChange}
                onDragEnd={handleDragEnd}
                onReset={handleDateReset}
                isLoading={isLoadingDateFilter}
                compact={compactDateRange}
              />
            </div>
          )}

          {/* 탭 컨텐츠 */}
          {currentTab === 'ai' && (
            <div className="mt-2 flex min-h-0 flex-1 flex-col overflow-hidden rounded-3xl border border-gray-200">
              {/* 필터 영역 */}
              <div className="flex h-10 shrink-0 items-center justify-between gap-2 border-b bg-gray-50 px-2 py-1">
                {aiViewMode === 'chart' ? (
                  <>
                    {/* 안정도 슬라이더 */}
                    {(() => {
                      // bounds 내로 clamp된 값 계산 (모드 전환 시 깜빡임 방지)
                      const clampedMin = Math.max(stabilityBounds.min, Math.min(stabilityBounds.max, stabilityRange[0]));
                      const clampedMax = Math.max(stabilityBounds.min, Math.min(stabilityBounds.max, stabilityRange[1]));
                      const range = stabilityBounds.max - stabilityBounds.min || 1;
                      return (
                        <div className="flex flex-1 items-center gap-3">
                          <span className="text-xs text-gray-500 whitespace-nowrap">{showDelta ? 'Δst' : '안정도'}</span>
                          <div className="relative flex-1 h-8">
                            {/* 트랙 배경 */}
                            <div className="absolute top-2 left-0 right-0 h-1 rounded bg-gray-200" />
                            {/* 선택 범위 표시 */}
                            <div
                              className="absolute top-2 h-1 rounded bg-primary"
                              style={{
                                left: `${((clampedMin - stabilityBounds.min) / range) * 100}%`,
                                right: `${((stabilityBounds.max - clampedMax) / range) * 100}%`
                              }}
                            />
                            {/* Min 슬라이더 */}
                            <input
                              type="range"
                              min={stabilityBounds.min}
                              max={stabilityBounds.max}
                              step="0.01"
                              value={clampedMin}
                              onChange={(e) => {
                                const newMin = parseFloat(e.target.value);
                                setStabilityRange([Math.min(newMin, stabilityRange[1] - 0.01), stabilityRange[1]]);
                              }}
                              className="absolute top-0 left-0 z-20 w-full h-5 cursor-pointer appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer"
                            />
                            {/* Max 슬라이더 */}
                            <input
                              type="range"
                              min={stabilityBounds.min}
                              max={stabilityBounds.max}
                              step="0.01"
                              value={clampedMax}
                              onChange={(e) => {
                                const newMax = parseFloat(e.target.value);
                                setStabilityRange([stabilityRange[0], Math.max(newMax, stabilityRange[0] + 0.01)]);
                              }}
                              className="absolute top-0 left-0 z-10 w-full h-5 cursor-pointer appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:pointer-events-auto [&::-moz-range-thumb]:appearance-none [&::-moz-range-thumb]:w-4 [&::-moz-range-thumb]:h-4 [&::-moz-range-thumb]:rounded-full [&::-moz-range-thumb]:bg-primary [&::-moz-range-thumb]:border-2 [&::-moz-range-thumb]:border-white [&::-moz-range-thumb]:shadow-md [&::-moz-range-thumb]:cursor-pointer"
                            />
                            {/* 값 표시 */}
                            <div className="absolute bottom-0 left-0 right-0 flex justify-between">
                              <span className="w-8 text-[9px] text-gray-400 tabular-nums">{clampedMin.toFixed(2)}</span>
                              <span className="w-8 text-[9px] text-gray-400 tabular-nums text-right">{clampedMax.toFixed(2)}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })()}

                    {/* 데이터 카운트 — 분모는 숨김 포함 전체(숨긴 개수는 '초기값 숨김' 라벨 옆에 표시) */}
                    <span className="w-16 text-xs text-gray-400 tabular-nums text-right">
                      {scatterData.length}/{scatterDataAll.length}
                    </span>

                    {/* 델타 체크박스 */}
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showDelta}
                        onChange={(e) => setShowDelta(e.target.checked)}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-xs text-gray-600">Δ</span>
                    </label>

                    {/* Y축 stability 토글 */}
                    <label className="flex items-center gap-1.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={useStabilityY}
                        onChange={(e) => setUseStabilityY(e.target.checked)}
                        className="h-3.5 w-3.5 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <span className="text-xs text-gray-600">Y:st</span>
                    </label>

                    {hideDefaultsToggle}

                    {/* 일별 재생 진입/종료 버튼(차트 전용). 진입=프리로드→자동재생, 이후 하단 스크러버로 조회. */}
                    <button
                      type="button"
                      onClick={() => (playbackPreloading || playbackIdx != null ? stopPlayback() : startPlayback())}
                      disabled={playbackDisabled && playbackIdx == null && !playbackPreloading}
                      title={availableTimelineDates.length < 2 ? '재생하려면 시험 날짜가 2일 이상 필요합니다' : '개념 좌표의 일별 변화 조회'}
                      className="flex shrink-0 items-center gap-1 rounded-3xl border border-gray-300 bg-white px-2 py-1 text-xs transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                    >
                      {playbackPreloading ? (
                        <>
                          <span className="h-3 w-3 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                          <span className="text-gray-600">{playbackProgress.loaded}/{playbackProgress.total}</span>
                        </>
                      ) : playbackIdx != null ? (
                        <>
                          <X className="h-3 w-3 text-primary" />
                          <span className="font-medium text-primary">종료</span>
                        </>
                      ) : (
                        <>
                          <Play className="h-3 w-3 text-gray-500" />
                          <span className="text-gray-600">재생</span>
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <>
                    <div className="ml-auto" />
                    {hideDefaultsToggle}
                  </>
                )}

                {/* 개념 추이 모달 진입 버튼 — 선택 개념의 직접 개념(r) 시계열. 뷰 모드와 무관하게 항상 표시. */}
                <button
                  type="button"
                  onClick={() => setShowTrendModal(true)}
                  disabled={availableTimelineDates.length < 2}
                  title={availableTimelineDates.length < 2 ? '추이를 보려면 시험 날짜가 2일 이상 필요합니다' : '선택한 개념의 직접 개념(r) 시계열 보기'}
                  className="flex shrink-0 items-center gap-1 rounded-3xl border border-gray-300 bg-white px-2 py-1 text-xs transition-colors hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <TrendingUp className="h-3 w-3 text-gray-500" />
                  <span className="text-gray-600">추이</span>
                </button>

                {/* 뷰 모드 토글 버튼 */}
                <button
                  onClick={() => setAiViewMode(v => v === 'chart' ? 'table' : 'chart')}
                  className="flex items-center gap-1 rounded-3xl border border-gray-300 bg-white px-2 py-1 text-xs transition-colors hover:bg-gray-100"
                >
                  {aiViewMode === 'table'
                    ? <ToggleRight className="h-3 w-3 text-primary" />
                    : <ToggleLeft className="h-3 w-3 text-gray-400" />}
                  <span className={aiViewMode === 'table' ? 'text-primary font-medium' : 'text-gray-600'}>
                    {aiViewMode === 'chart' ? '테이블' : '그래프'}
                  </span>
                </button>
              </div>

              {/* 차트/테이블 영역 */}
              {aiViewMode === 'chart' ? (
                <>
                  {/* 통계 요약 - 그래프 뷰에서만 표시 (students-hub 등에서는 hideEngineSummary로 숨김) */}
                  {engineData && !hideEngineSummary && (
                    <div className="flex items-center gap-4 overflow-x-auto border-b border-gray-200 bg-gray-50 px-2 py-1 text-xs whitespace-nowrap text-gray-700">
                      <span><span className="font-semibold">exam</span>: {engineData.exam_counts}</span>
                      <span><span className="font-semibold">solved</span>: {engineData.solved_problems}</span>
                      <span><span className="font-semibold">unique</span>: {engineData.total_problems}</span>
                      <span><span className="font-semibold">unlabeled</span>: {engineData.unlabeled_problems}</span>
                      {engineData.delta?.exam?.exam_seq && (
                        <span><span className="font-semibold">delta</span>: last_exam #{engineData.delta.exam.exam_seq}</span>
                      )}
                      <span className="text-gray-500">* 정렬: 기본 (concept_index)</span>
                      {onConceptSelect && allFirstTags.length > 0 && (
                        <span className="ml-auto flex shrink-0 items-center gap-1">
                          <span className="font-semibold text-gray-700">태그</span>
                          <TagFilterSelect tags={allFirstTags} value={selectedTag} onChange={setSelectedTag} />
                        </span>
                      )}
                    </div>
                  )}
                  <div className={stackChartTable ? 'flex min-h-0 flex-1 flex-col gap-1 overflow-hidden' : 'flex flex-1 gap-1 overflow-hidden'}>
                    {/* stackChartTable: 차트(필터+산점도)를 한 행으로 묶고 테이블을 아래로. 기본값은 display:contents 로 기존 레이아웃 유지.
                        세로 몫은 차트 9 : 테이블 11 — 반반(1:1)에서 차트를 10% 줄인 만큼을 테이블에 준다. 테이블 쪽 flex-[11_11_0%] 와 짝이다 */}
                    <div className={stackChartTable ? 'flex min-h-0 flex-[9_9_0%] gap-1 overflow-hidden' : 'contents'}>
                    {/* 차트 + 플로팅 필터 드로어 */}
                    <div className="relative flex min-h-0 flex-1">
                      {showFilterPanel && (
                        <div
                          className="absolute inset-0 z-10"
                          onClick={() => setShowFilterPanel(false)}
                        />
                      )}
                      {/* 플로팅 필터 드로어 (핸들과 함께 슬라이드) */}
                      <div
                        className={`absolute left-0 top-0 z-20 h-full transition-transform duration-200 ease-in-out ${showFilterPanel ? 'translate-x-0' : '-translate-x-full'}`}
                      >
                        {/* 필터 패널 본체 */}
                    <div className="h-full overflow-auto border-r border-gray-200 bg-white py-1 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300" style={{ minWidth: '110px', width: 'fit-content', scrollbarWidth: 'thin', scrollbarColor: '#d1d5db transparent' }}>
                      <div className="px-1">
                        <div className="mb-1 flex items-center justify-between px-0.5">
                          <span className="text-[10px] font-semibold text-gray-500">Math Type</span>
                          <button
                            onClick={toggleAllMathTypes}
                            className="text-primary hover:text-primary/70"
                          >
                            <ListChecks className="h-3 w-3" />
                          </button>
                        </div>
                        {mathTypeList.map(type => (
                          <button
                            key={type.code}
                            onClick={() => toggleMathTypeFilter(type.code)}
                            className="flex w-full items-center gap-1 rounded px-1 py-1 text-xs hover:bg-gray-50"
                          >
                            <span
                              className="inline-flex h-4 w-7 items-center justify-center rounded text-[9px] font-semibold"
                              style={{ backgroundColor: getMathTypeColor(type.code) }}
                            >
                              {type.code}
                            </span>
                            <span className="flex-1 text-left">{type.name}</span>
                            {selectedMathTypes.includes(type.code) && (
                              <Check className="h-2.5 w-2.5 text-primary" />
                            )}
                          </button>
                        ))}
                      </div>
                      <div className="mx-1 my-1 border-t border-gray-100" />
                      <div className="px-1">
                        <div className="mb-1 flex items-center justify-between px-0.5">
                          <span className="text-[10px] font-semibold text-gray-500">Concept Type</span>
                          <button
                            onClick={toggleAllConceptTypes}
                            className="text-primary hover:text-primary/70"
                          >
                            <ListChecks className="h-3 w-3" />
                          </button>
                        </div>
                        {conceptTypes.map(type => (
                          <button
                            key={type.code}
                            onClick={() => toggleConceptTypeFilter(type.code)}
                            className="flex w-full items-center gap-1 rounded px-1 py-1 text-xs hover:bg-gray-50"
                          >
                            <span
                              className="inline-flex h-4 w-7 items-center justify-center rounded text-[9px] font-semibold"
                              style={{ backgroundColor: getConceptTypeColor(type.code) }}
                            >
                              {type.code}
                            </span>
                            <span className="flex-1 text-left">{type.name}</span>
                            {selectedTypes.includes(type.code) && (
                              <Check className="h-2.5 w-2.5 text-primary" />
                            )}
                          </button>
                        ))}
                      </div>
                      {totalSelectedFilters > 0 && (
                        <>
                          <div className="mx-2 my-1 border-t border-gray-100" />
                          <button
                            onClick={resetAllFilters}
                            className="w-full px-3 py-1 text-xs text-red-500 hover:bg-red-50"
                          >
                            필터 초기화
                          </button>
                        </>
                      )}
                    </div>
                    {/* 우측 가장자리 핸들 (패널과 함께 슬라이드) */}
                    <button
                      onClick={() => setShowFilterPanel(v => !v)}
                      title={showFilterPanel ? '필터 닫기' : '필터 열기'}
                      className="absolute left-full top-1/2 z-20 flex -translate-y-1/2 flex-col items-center gap-1 rounded-r-md border border-l-0 border-gray-200 bg-white px-0.5 py-2 shadow-md hover:bg-gray-50"
                    >
                      <ChevronRight className={`h-3.5 w-3.5 text-gray-500 transition-transform ${showFilterPanel ? 'rotate-180' : ''}`} />
                      {totalSelectedFilters > 0 && (
                        <span className="rounded-full bg-primary px-1 text-[9px] leading-none text-white">
                          {totalSelectedFilters}
                        </span>
                      )}
                    </button>
                      </div>
                    {/* 차트 영역 */}
                    {(isLoadingEngine || !engineData) ? (
                      <div className="flex flex-1 items-center justify-center text-gray-500">
                        <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                      </div>
                    ) : (scatterData.length > 0 || playbackIdx != null) ? (
                      /* 재생 중엔 표시 개념이 0개인 프레임(초기값 숨김 + 필터)에서도 컨테이너를 유지한다 —
                         날짜 라벨·스크러버가 이 안에 있어 언마운트되면 되감기 자체가 불가능해진다. */
                      <div
                        ref={scatterContainerRef}
                        className={`relative flex-1${stackChartTable ? '' : ' h-[90%]'}${dragRect ? ' cursor-crosshair select-none' : ''}`}
                        onMouseDown={handleDragMouseDown}
                        onClickCapture={handleContainerClickCapture}
                      >
                  {/* 재생 중 현재 날짜 라벨(좌상단 오버레이) */}
                  {playbackIdx != null && timelineDates[playbackIdx] && (
                    <div className="pointer-events-none absolute left-2 top-2 z-20 rounded-md bg-white/85 px-2 py-1 text-xs font-semibold text-gray-700 shadow-sm ring-1 ring-gray-200">
                      {timelineDates[playbackIdx]}
                    </div>
                  )}
                  {/* 이 프레임에 그릴 점이 없음 — 스크러버는 살려두고 사유만 알린다 */}
                  {scatterData.length === 0 && (
                    <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center text-xs text-gray-400">
                      {emptyReason}
                    </div>
                  )}
                  {/* 날짜 스크러버(재생 중) — 자동재생 토글 + 날짜 슬라이더로 직접 조회 */}
                  {playbackIdx != null && timelineDates.length > 1 && (
                    <div
                      className="absolute bottom-3 left-1/2 z-20 flex -translate-x-1/2 items-center gap-2 rounded-full bg-white/45 px-3 py-1.5 backdrop-blur-[2px]"
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        onClick={togglePlayback}
                        className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-primary hover:bg-primary/10"
                        title={isPlaying ? '일시정지' : '재생'}
                      >
                        {isPlaying ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                      </button>
                      <input
                        type="range"
                        min={0}
                        max={timelineDates.length - 1}
                        step={1}
                        value={playbackIdx}
                        onChange={(e) => scrubTo(Number(e.target.value))}
                        className="w-40 cursor-pointer accent-primary opacity-50 sm:w-56"
                        aria-label="날짜 스크러버"
                      />
                      <span className="w-9 shrink-0 text-right text-[11px] tabular-nums text-gray-500">
                        {playbackIdx + 1}/{timelineDates.length}
                      </span>
                    </div>
                  )}
                  <ResponsiveContainer
                    width="100%"
                    height="100%"
                    onResize={handleChartResize}
                  >
                    <ScatterChart margin={{ top: 10, right: 10, bottom: 10, left: 10 }}>
                      <XAxis
                        type="number"
                        dataKey="plotX"
                        domain={[axisRange.xMin, axisRange.xMax]}
                        name={showDelta ? 'Δr' : 'r'}
                        height={0}
                        axisLine={false}
                        tickLine={false}
                        tick={false}
                      />
                      <YAxis
                        type="number"
                        dataKey="plotY"
                        domain={[axisRange.yMin, axisRange.yMax]}
                        name={useStabilityY ? (showDelta ? 'Δst' : 'st') : (showDelta ? 'Δh' : 'h')}
                        width={0}
                        axisLine={false}
                        tickLine={false}
                        tick={false}
                      />
                      {/* r·h 기준 눈금선 (0.1 간격; 0.5 배수는 진하게) */}
                      {gridLines.xs.map(v => (
                        <ReferenceLine key={`gx${v}`} x={v} stroke={Math.round(Math.abs(v) * 10) % 5 === 0 ? '#cbd5e1' : '#edeff2'} strokeDasharray="3 3" />
                      ))}
                      {gridLines.ys.map(v => (
                        <ReferenceLine key={`gy${v}`} y={v} stroke={Math.round(Math.abs(v) * 10) % 5 === 0 ? '#cbd5e1' : '#edeff2'} strokeDasharray="3 3" />
                      ))}
                      {/* 중심 x·y축 */}
                      <ReferenceLine x={0} stroke="#6b7280" strokeWidth={1.5} />
                      <ReferenceLine y={0} stroke="#6b7280" strokeWidth={1.5} />
                      {/* 빈 Scatter로 축 범위 유지 */}
                      <Scatter data={scatterData} isAnimationActive={false} fill="transparent" />
                    </ScatterChart>
                  </ResponsiveContainer>

                  {/* 커스텀 점 오버레이 */}
                  {chartDimensions.width > 0 && (
                    <svg
                      className="pointer-events-none absolute inset-0"
                      width={chartDimensions.width}
                      height={chartDimensions.height}
                      style={{ overflow: 'visible' }}
                    >
                      {/* 반원 2색 세로분할 gradient(색쌍별 1개). 알파는 stop-opacity로 보존 → fill=rgba와 픽셀 동일. */}
                      <defs>
                        {[...dotGradients].map(([id, c]) => (
                          <linearGradient key={id} id={id} x1="0" y1="0" x2="1" y2="0">
                            <stop offset="0" stopColor={c.left.color} stopOpacity={c.left.opacity} />
                            <stop offset="0.5" stopColor={c.left.color} stopOpacity={c.left.opacity} />
                            <stop offset="0.5" stopColor={c.right.color} stopOpacity={c.right.opacity} />
                            <stop offset="1" stopColor={c.right.color} stopOpacity={c.right.opacity} />
                          </linearGradient>
                        ))}
                      </defs>
                      {/* 단독 점 렌더링 (선택된 점을 뒤에 그려 맨 앞으로) */}
                      {[...clusteredData.singles].sort((a, b) => {
                        const aSel = selectedPoint === a.cid || selectedConceptCids.includes(a.cid);
                        const bSel = selectedPoint === b.cid || selectedConceptCids.includes(b.cid);
                        return (aSel ? 1 : 0) - (bSel ? 1 : 0);
                      }).map((point) => {
                        const isSelected = selectedPoint === point.cid || selectedConceptCids.includes(point.cid);
                        const gid = dotGradId(getMathTypeColor(point.mathType), getConceptTypeColor(point.conceptType));
                        return (
                          <g
                            key={point.cid}
                            className="pointer-events-auto cursor-pointer"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePointClick(point);
                            }}
                            style={{
                              // 위치=translate, 크기=scale(r). 단위 기하라 d/gradient가 프레임 불변 →
                              // 재생 시 브라우저가 transform만 보간(경량). 활공은 slideOn(타임라인 중·좌표계 불변)일 때만.
                              transform: `translate(${point.screenX}px, ${point.screenY}px) scale(${point.radius})`,
                              transformBox: 'view-box',
                              transformOrigin: '0 0',
                              transition: slideOn ? `transform ${PLAYBACK_GLIDE_MS}ms cubic-bezier(0.22,1,0.36,1)` : 'none',
                              willChange: playbackIdx != null ? 'transform' : undefined,
                            }}
                          >
                            {/* 단위원(r=1): 좌=mathType / 우=conceptType 세로분할 gradient 1개(노드 절반) */}
                            <circle r={1} fill={`url(#${gid})`} />
                            {/* 선택 시에만 외곽선(강조) — scale 무관 2.5px 유지 */}
                            {isSelected && (
                              <circle r={1} fill="none" stroke="#1e40af" strokeWidth={2.5} vectorEffect="non-scaling-stroke" />
                            )}
                          </g>
                        );
                      })}

                      {/* 중심 축(원점 교차선) 라벨 — x/y축 이름을 중심선에 표시 */}
                      {(() => {
                        const origin = getScreenCoords({ plotX: 0, plotY: 0 });
                        if (!origin) return null;
                        const plotRight = chartDimensions.width - 10;
                        const plotTop = 10;
                        const xLabel = showDelta ? 'Δ직접 개념' : '직접 개념';
                        const yLabel = useStabilityY ? (showDelta ? 'Δst' : 'st') : (showDelta ? 'Δ연관 개념' : '연관 개념');
                        return (
                          <>
                            <text x={plotRight} y={origin.y - 6} fontSize={12} fontWeight={600} textAnchor="end" fill="#374151">{xLabel}</text>
                            <text x={origin.x + 8} y={plotTop + 4} fontSize={12} fontWeight={600} textAnchor="start" fill="#374151" writingMode="vertical-rl">{yLabel}</text>
                          </>
                        );
                      })()}
                      {/* r·h 수치 눈금 (0.1 단위, 중심축 위 흰색 헤일로로 가독성 확보) */}
                      {(() => {
                        const origin = getScreenCoords({ plotX: 0, plotY: 0 });
                        if (!origin) return null;
                        return (
                          <>
                            {gridLines.xs.map(v => {
                              const p = getScreenCoords({ plotX: v, plotY: 0 });
                              if (!p) return null;
                              return (
                                <text key={`nx${v}`} x={p.x} y={origin.y + 11} fontSize={7} textAnchor="middle" fill="#6b7280" stroke="#ffffff" strokeWidth={2.5} paintOrder="stroke">{v}</text>
                              );
                            })}
                            {gridLines.ys.map(v => {
                              const p = getScreenCoords({ plotX: 0, plotY: v });
                              if (!p) return null;
                              return (
                                <text key={`ny${v}`} x={origin.x - 5} y={p.y + 2.5} fontSize={7} textAnchor="end" fill="#6b7280" stroke="#ffffff" strokeWidth={2.5} paintOrder="stroke">{v}</text>
                              );
                            })}
                          </>
                        );
                      })()}
                    </svg>
                  )}

                  {/* 드래그 선택 사각형(rubber-band) — 그리기 전용 레이어(pointer-events-none), dot 위·툴팁(z-50) 아래 */}
                  {dragRect && (
                    <div className="pointer-events-none absolute inset-0 z-20">
                      <div
                        className="absolute rounded-sm border-2 border-primary/70 bg-primary/10"
                        style={{
                          left: Math.min(dragRect.x0, dragRect.x1),
                          top: Math.min(dragRect.y0, dragRect.y1),
                          width: Math.abs(dragRect.x1 - dragRect.x0),
                          height: Math.abs(dragRect.y1 - dragRect.y0),
                        }}
                      />
                    </div>
                  )}

                  {/* 호버 툴팁 (선택된 점) */}
                  {selectedPoint && (() => {
                    if (playbackIdx != null) return null; // 재생/스크럽 중엔 툴팁이 목표좌표로 순간이동 → 숨김(선택 링은 점 따라 활공)
                    const point = scatterData.find(p => p.cid === selectedPoint);
                    if (!point) return null;
                    const coords = getScreenCoords(point);
                    if (!coords) return null;

                    // 점의 반지름 계산
                    const pointRadius = getRelativeRadius(point.plotZ ?? 0);
                    const padding = 8; // 점과 팝업 사이 여백
                    const offset = pointRadius + padding;

                    // 팝업 예상 크기
                    const popupWidth = 420;
                    const popupHeight = 40;

                    // 차트 영역 크기 (scatterContainerRef 기준)
                    const chartWidth = chartDimensions.width || 400;
                    const chartHeight = chartDimensions.height || 300;

                    // 좌우 위치 결정: 오른쪽 공간이 부족하면 왼쪽에 표시
                    const spaceRight = chartWidth - coords.x;
                    const spaceLeft = coords.x;
                    const showOnRight = spaceRight >= popupWidth + offset || spaceRight >= spaceLeft;

                    // 상하 위치 결정: 아래쪽 공간이 부족하면 위쪽에 표시
                    const spaceBottom = chartHeight - coords.y;
                    const spaceTop = coords.y;
                    const showOnBottom = spaceBottom >= popupHeight + offset || spaceBottom >= spaceTop;

                    // 최종 위치 계산 (점을 피해서)
                    let left, top;
                    let transformX = '0', transformY = '0';

                    if (showOnRight) {
                      left = coords.x + offset;
                    } else {
                      left = coords.x - offset;
                      transformX = '-100%';
                    }

                    if (showOnBottom) {
                      top = coords.y;
                    } else {
                      top = coords.y;
                      transformY = '-100%';
                    }

                    return (
                      <div
                        className="absolute z-50 rounded-lg border bg-white px-3 py-2 shadow-lg"
                        style={{
                          left,
                          top,
                          transform: `translate(${transformX}, ${transformY})`
                        }}
                      >
                        <div className="flex items-start gap-2">
                          <div
                            className="flex items-center gap-1 rounded-3xl border border-gray-200 px-2 py-0.5 text-xs text-gray-700 cursor-pointer hover:bg-gray-50 transition-colors whitespace-nowrap"
                            onClick={() => { setSelectedConceptId(point.cid); setShowMathceptModal(true); }}
                            title="클릭하여 상세 정보 보기"
                          >
                            <span
                              className="inline-flex items-center justify-center font-semibold text-gray-700"
                              style={{ backgroundColor: 'rgba(156, 163, 175, 0.3)', borderRadius: '6px', width: '24px', height: '16px', minWidth: '24px', fontSize: '9px' }}
                            >
                              {point.order}
                            </span>
                            {point.mathType && (
                              <span
                                className="inline-flex items-center justify-center shrink-0 rounded-md px-1 font-semibold text-gray-700"
                                style={{ backgroundColor: getMathTypeColor(point.mathType), minWidth: '24px', height: '16px', fontSize: '9px' }}
                              >
                                {point.mathType}
                              </span>
                            )}
                            {point.conceptType && (
                              <span
                                className="inline-flex items-center justify-center font-semibold text-gray-800"
                                style={{ backgroundColor: getConceptTypeColor(point.conceptType), borderRadius: '6px', width: '24px', height: '16px', minWidth: '24px', fontSize: '8px' }}
                              >
                                {point.conceptType}
                              </span>
                            )}
                            <span dangerouslySetInnerHTML={{ __html: point.name }} />
                            <span className="ml-auto shrink-0 text-[10px] text-gray-400">
                              {showDelta ? 'Δ' : ''}r={point.plotX?.toFixed(2)}, {useStabilityY ? (showDelta ? 'Δst' : 'st') : (showDelta ? 'Δh' : 'h')}={point.plotY?.toFixed(2)}{!useStabilityY && <>, {showDelta ? 'Δ' : ''}st={point.plotZ?.toFixed(2)}</>}
                            </span>
                          </div>
                          <button
                            onClick={() => setSelectedPoint(null)}
                            className="shrink-0 text-gray-400 hover:text-gray-600"
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              ) : (
                      <div className="flex flex-1 items-center justify-center text-gray-400">
                        {emptyReason}
                      </div>
                    )}
                      </div>
                    </div>
                    {/* 차트 옆 테이블(기본) / 차트 아래 테이블(stackChartTable).
                        학습 화면 전용 두 자리(stackedTableHeader·stackedTableAlt, 기본 null — 다른 화면은 이 분기를 타지 않는다, 설계 FE-20260914 §7):
                        머리 띠를 주면 표 칸 안에서 표 위에 두고, 대체 보기를 주면 머리 띠·표 대신 그것을 같은 칸에 그린다.
                        대체 보기 동안 표는 내린다 — 정렬·필터는 이 부품의 state 라 다시 올리면 그대로이고, 표 스크롤 함수는 표가 없으면 멈춘다 */}
                    {stackChartTable && stackedTableAlt ? (
                      <div className="flex min-h-0 flex-[11_11_0%] flex-col overflow-hidden">{stackedTableAlt}</div>
                    ) : (
                    <div className={stackChartTable && stackedTableHeader ? 'flex min-h-0 flex-[11_11_0%] flex-col gap-1' : 'contents'}>
                    {stackChartTable && stackedTableHeader}
                    <div ref={chartSideTableRef} className={`${stackChartTable ? (stackedTableHeader ? 'flex min-h-0 flex-1 flex-col' : 'flex min-h-0 flex-[11_11_0%] flex-col') : 'hidden xl:flex xl:flex-col flex-1'} bg-white rounded border border-gray-200 overflow-auto [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300`} style={{ scrollbarWidth: 'thin', scrollbarColor: '#d1d5db transparent' }}>
                      <table className="ai-tracker-table w-full border-separate border-spacing-0 text-xs">
                        <thead className="sticky top-0 z-10">
                          <tr className="bg-blue-50">
                            <th className="border-b border-gray-200 px-2 py-1.5 text-left font-semibold text-gray-700">
                              <div className="flex items-center justify-between gap-1">
                                <span className="flex items-center gap-1">
                                  <button
                                    type="button"
                                    onClick={() => handleTableSortClick('order', chartSideTableSort, setChartSideTableSort)}
                                    title="순번(order) 오름/내림차순 정렬"
                                    className="cursor-pointer select-none hover:text-gray-900"
                                  >
                                    {chartSideTableSort.column === 'order'
                                      ? (chartSideTableSort.direction === 'desc' ? '▼' : '▲')
                                      : '↕'}
                                  </button>
                                  <span>수학개념</span>
                                  {onConceptSelect && (
                                    <button
                                      type="button"
                                      onClick={() => setShowSelectedOnly(v => !v)}
                                      title={showSelectedOnly ? '선택된 개념만 표시 중 (클릭 시 전체 표시)' : '전체 표시 중 (클릭 시 선택된 개념만 표시)'}
                                      className={`shrink-0 whitespace-nowrap rounded border px-1 py-0.5 text-[10px] font-normal transition-colors ${showSelectedOnly ? 'border-primary/40 bg-primary/10 text-primary' : 'border-gray-200 bg-gray-50 text-gray-500 hover:text-gray-700'}`}
                                    >
                                      {showSelectedOnly ? '전체' : '선택만'}
                                    </button>
                                  )}
                                </span>
                                {onConceptSelect && <TagFilterSelect tags={allFirstTags} value={selectedTag} onChange={setSelectedTag} />}
                              </div>
                            </th>
                            {['r', 'h', 'st'].map(col => (
                              <th
                                key={col}
                                className="w-14 cursor-pointer border-b border-gray-200 px-2 py-1.5 text-right font-semibold text-gray-700 hover:bg-blue-100 select-none whitespace-nowrap"
                                onClick={(e) => openColumnFilter(col, e.currentTarget, chartSideTableSort, setChartSideTableSort)}
                              >
                                {COLUMN_LABELS[col]} {chartSideTableSort.column === col ? (chartSideTableSort.direction === 'desc' ? '▼' : '▲') : '↕'}
                                {isRangeFiltered(col) && <span className="ml-0.5 inline-block h-1.5 w-1.5 rounded-full bg-primary align-middle" />}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {chartSideVisibleRows.map((row) => (
                            <ConceptTableRow key={row.cid} row={row} onClick={handleConceptRowClick} selectedCids={chartSideSelectedCids} />
                          ))}
                          {/* 0행 사유 안내 — 선택 개념·태그의 개념이 초기값 숨김/범위 필터/현재 프레임으로 전부 빠진 경우.
                              이 표는 차트와 달리 로딩 게이트 밖에 있어 로딩 중 오안내가 되지 않게 직접 가드한다. */}
                          {chartSideVisibleRows.length === 0 && !isLoadingEngine && engineData && (
                            <tr>
                              <td colSpan={4} className="px-2 py-3 text-center text-[11px] leading-relaxed text-gray-400">
                                {showSelectedOnly
                                  ? '선택한 개념이 현재 필터·초기값 숨김에서는 표시되지 않습니다'
                                  : emptyReason}
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                    </div>
                    )}
                  </div>
                </>
              ) : (
                /* 테이블 뷰 */
                (isLoadingEngine || !engineData) ? (
                  <div className="flex flex-1 items-center justify-center text-gray-500">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                  </div>
                ) : scatterDataRaw.length > 0 ? (
                  <div className="flex flex-1 flex-col gap-2 overflow-hidden p-2 min-[1440px]:flex-row">
                    {/* 테이블 1 (모바일: 단독 / 데스크톱: 좌측) */}
                    <div className="flex-1 overflow-auto rounded border border-gray-200 min-[1440px]:block hidden [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300" style={{ scrollbarWidth: 'thin', scrollbarColor: '#d1d5db transparent' }}>
                      <table className="ai-tracker-table w-full min-w-[450px] border-separate border-spacing-0 text-xs">
                        <thead className="sticky top-0 z-10">
                          <tr className="bg-blue-50">
                            <th className="border-b border-gray-200 px-2 py-1.5 text-left font-semibold text-gray-700">
                              <div className="flex items-center justify-between gap-1">
                                <span>수학개념 (테이블1)</span>
                                {onConceptSelect && <TagFilterSelect tags={allFirstTags} value={selectedTag} onChange={setSelectedTag} />}
                              </div>
                            </th>
                            {['r', 'h', 'st'].map(col => (
                              <th
                                key={col}
                                className="w-14 cursor-pointer border-b border-gray-200 px-2 py-1.5 text-right font-semibold text-gray-700 hover:bg-blue-100 select-none whitespace-nowrap"
                                onClick={(e) => openColumnFilter(col, e.currentTarget, rTableSort, setRTableSort)}
                              >
                                {COLUMN_LABELS[col]} {rTableSort.column === col ? (rTableSort.direction === 'desc' ? '▼' : '▲') : '↕'}
                                {isRangeFiltered(col) && <span className="ml-0.5 inline-block h-1.5 w-1.5 rounded-full bg-primary align-middle" />}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rSortedRows.map((row) => (
                            <ConceptTableRow key={row.cid} row={row} onClick={handleConceptRowClick} selectedCids={selectedConceptCids} />
                          ))}
                          {rSortedRows.length === 0 && emptyFilterRow}
                        </tbody>
                      </table>
                    </div>
                    {/* 테이블 2 (데스크톱: 우측) / 모바일: 단독 테이블 */}
                    <div className="flex-1 overflow-auto rounded border border-gray-200 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300" style={{ scrollbarWidth: 'thin', scrollbarColor: '#d1d5db transparent' }}>
                      <table className="ai-tracker-table w-full min-w-[450px] border-separate border-spacing-0 text-xs">
                        <thead className="sticky top-0 z-10">
                          <tr className="bg-green-50">
                            <th className="border-b border-gray-200 px-2 py-1.5 text-left font-semibold text-gray-700">
                              <div className="flex items-center justify-between gap-1">
                                <span><span className="min-[1440px]:hidden">수학개념</span><span className="hidden min-[1440px]:inline">수학개념 (테이블2)</span></span>
                                {onConceptSelect && <TagFilterSelect tags={allFirstTags} value={selectedTag} onChange={setSelectedTag} />}
                              </div>
                            </th>
                            {['r', 'h', 'st'].map(col => (
                              <th
                                key={col}
                                className="w-14 cursor-pointer border-b border-gray-200 px-2 py-1.5 text-right font-semibold text-gray-700 hover:bg-green-100 select-none whitespace-nowrap"
                                onClick={(e) => openColumnFilter(col, e.currentTarget, hTableSort, setHTableSort)}
                              >
                                {COLUMN_LABELS[col]} {hTableSort.column === col ? (hTableSort.direction === 'desc' ? '▼' : '▲') : '↕'}
                                {isRangeFiltered(col) && <span className="ml-0.5 inline-block h-1.5 w-1.5 rounded-full bg-primary align-middle" />}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {hSortedRows.map((row) => (
                            <ConceptTableRow key={row.cid} row={row} onClick={handleConceptRowClick} selectedCids={selectedConceptCids} />
                          ))}
                          {hSortedRows.length === 0 && emptyFilterRow}
                        </tbody>
                      </table>
                    </div>
                    {/* 테이블 3 (xl 이상에서만 표시, st 내림차순 기본) */}
                    <div className="hidden min-[2060px]:block flex-1 overflow-auto rounded border border-gray-200 [&::-webkit-scrollbar]:w-1.5 [&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-300" style={{ scrollbarWidth: 'thin', scrollbarColor: '#d1d5db transparent' }}>
                      <table className="ai-tracker-table w-full min-w-[450px] border-separate border-spacing-0 text-xs">
                        <thead className="sticky top-0 z-10">
                          <tr className="bg-purple-50">
                            <th className="border-b border-gray-200 px-2 py-1.5 text-left font-semibold text-gray-700">
                              <div className="flex items-center justify-between gap-1">
                                <span>수학개념 (테이블3)</span>
                                {onConceptSelect && <TagFilterSelect tags={allFirstTags} value={selectedTag} onChange={setSelectedTag} />}
                              </div>
                            </th>
                            {['r', 'h', 'st'].map(col => (
                              <th
                                key={col}
                                className="w-14 cursor-pointer border-b border-gray-200 px-2 py-1.5 text-right font-semibold text-gray-700 hover:bg-purple-100 select-none whitespace-nowrap"
                                onClick={(e) => openColumnFilter(col, e.currentTarget, stTableSort, setStTableSort)}
                              >
                                {COLUMN_LABELS[col]} {stTableSort.column === col ? (stTableSort.direction === 'desc' ? '▼' : '▲') : '↕'}
                                {isRangeFiltered(col) && <span className="ml-0.5 inline-block h-1.5 w-1.5 rounded-full bg-primary align-middle" />}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {stSortedRows.map((row) => (
                            <ConceptTableRow key={row.cid} row={row} onClick={handleConceptRowClick} selectedCids={selectedConceptCids} />
                          ))}
                          {stSortedRows.length === 0 && emptyFilterRow}
                        </tbody>
                      </table>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-1 items-center justify-center text-gray-400">
                    {emptyReason}
                  </div>
                )
              )}

              {/* 컬럼 필터 팝오버 */}
              {columnFilterPopup && createPortal(
                (() => {
                  const { column, rect, sortState, setSortFn } = columnFilterPopup;
                  const bounds = rhstBounds[column];
                  const range = column === 'r' ? rRange : column === 'h' ? hRange : stRange;
                  const setRange = column === 'r' ? setRRange : column === 'h' ? setHRange : setStRange;
                  const bMin = bounds[0];
                  const bMax = bounds[1];
                  const bRange = bMax - bMin || 1;
                  const clampedMin = Math.max(bMin, Math.min(bMax, range[0]));
                  const clampedMax = Math.max(bMin, Math.min(bMax, range[1]));

                  return (
                    <div
                      className="fixed inset-0 z-[9999]"
                      onClick={() => setColumnFilterPopup(null)}
                    >
                      <div
                        className="absolute z-50 w-52 rounded-lg border bg-white p-3 shadow-lg"
                        style={{
                          top: rect.bottom + 4,
                          left: Math.max(8, Math.min(rect.left + rect.width / 2 - 104, window.innerWidth - 208 - 8))
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {/* 범위 슬라이더 */}
                        <div className="mb-2">
                          <div className="mb-1 text-[10px] font-semibold text-gray-500">{column} 범위</div>
                          <div className="relative h-8">
                            <div className="absolute top-2 left-0 right-0 h-1 rounded bg-gray-200" />
                            <div
                              className="absolute top-2 h-1 rounded bg-primary"
                              style={{
                                left: `${((clampedMin - bMin) / bRange) * 100}%`,
                                right: `${((bMax - clampedMax) / bRange) * 100}%`
                              }}
                            />
                            <input
                              type="range"
                              min={bMin}
                              max={bMax}
                              step={0.01}
                              value={clampedMin}
                              onChange={(e) => {
                                const v = parseFloat(e.target.value);
                                setRange([Math.min(v, range[1] - 0.01), range[1]]);
                              }}
                              className="absolute top-0 left-0 z-20 w-full h-5 cursor-pointer appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer"
                            />
                            <input
                              type="range"
                              min={bMin}
                              max={bMax}
                              step={0.01}
                              value={clampedMax}
                              onChange={(e) => {
                                const v = parseFloat(e.target.value);
                                setRange([range[0], Math.max(v, range[0] + 0.01)]);
                              }}
                              className="absolute top-0 left-0 z-10 w-full h-5 cursor-pointer appearance-none bg-transparent pointer-events-none [&::-webkit-slider-thumb]:pointer-events-auto [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5 [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-primary [&::-webkit-slider-thumb]:border-2 [&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:shadow-md [&::-webkit-slider-thumb]:cursor-pointer"
                            />
                            <div className="absolute bottom-0 left-0 right-0 flex justify-between">
                              <span className="text-[9px] text-gray-400 tabular-nums">{clampedMin.toFixed(2)}</span>
                              <span className="text-[9px] text-gray-400 tabular-nums">{clampedMax.toFixed(2)}</span>
                            </div>
                          </div>
                          <button
                            className="mt-1.5 w-full rounded bg-gray-100 px-2 py-0.5 text-[10px] text-gray-500 hover:bg-gray-200"
                            onClick={() => setRange([bMin, bMax])}
                          >
                            범위 초기화
                          </button>
                        </div>

                        {/* 정렬 */}
                        <div>
                          <div className="mb-1 text-[10px] font-semibold text-gray-500">{column} 정렬</div>
                          <div className="flex gap-1">
                            {[
                              { dir: 'asc', label: '▲ 오름차순' },
                              { dir: 'desc', label: '▼ 내림차순' },
                            ].map(({ dir, label }) => (
                              <button
                                key={dir}
                                className={`flex-1 rounded px-2 py-1 text-[10px] font-medium transition-colors ${
                                  sortState.column === column && sortState.direction === dir
                                    ? 'bg-primary text-white'
                                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                }`}
                                onClick={() => {
                                  if (sortState.column === column && sortState.direction === dir) {
                                    setSortFn({ column: null, direction: null });
                                  } else {
                                    setSortFn({ column, direction: dir });
                                  }
                                  setColumnFilterPopup(null);
                                }}
                              >
                                {label}
                              </button>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })(),
                document.body
              )}
            </div>
          )}

          {currentTab === 'rawdata' && (
            <>
              {/* 범례 및 옵션 */}
              <div className="mt-2 flex shrink-0 flex-wrap items-center justify-between gap-2 rounded-lg bg-gray-50 py-1 px-2">
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-1">
                    <div className="h-3 w-3 rounded bg-primary"></div>
                    <span className="text-xs">정답</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="h-3 w-3 rounded bg-red-500"></div>
                    <span className="text-xs">오답</span>
                  </div>
                </div>

                {/* 스냅샷 버튼 */}
                <button
                  onClick={handleSnapshot}
                  disabled={hasSnapshotted || tableData.length === 0}
                  className={`flex h-[25px] items-center gap-1.5 rounded-3xl px-3 text-xs font-medium transition-colors ${
                    hasSnapshotted || tableData.length === 0
                      ? 'cursor-not-allowed bg-gray-300 text-gray-500'
                      : 'bg-primary text-white hover:bg-primary/90'
                  }`}
                  title={hasSnapshotted ? '이미 스냅샷을 저장했습니다' : '현재 순서 스냅샷 저장'}
                >
                  <Camera className="h-3 w-3" />
                  <span>스냅샷</span>
                  {Object.keys(snapshots).length > 0 && (
                    <span className="rounded-full bg-white/20 px-1.5 text-xs">
                      {Math.max(...Object.values(snapshots).map(arr => arr.length))}
                    </span>
                  )}
                </button>

                <button
                  onClick={() => setShowAllConcepts(!showAllConcepts)}
                  className="flex items-center gap-1.5 rounded-3xl border border-gray-300 bg-white px-3 py-0.5 text-xs transition-colors hover:bg-gray-100"
                >
                  {showAllConcepts ? (
                    <ToggleRight className="h-3 w-3 text-primary" />
                  ) : (
                    <ToggleLeft className="h-3 w-3 text-gray-400" />
                  )}
                  <span className={showAllConcepts ? 'text-primary font-medium' : 'text-gray-600'}>
                    모든 개념
                  </span>
                </button>
              </div>

              {/* 테이블 영역(기존) */}
              <div className="mt-2 min-h-0 flex-1 overflow-y-auto overflow-x-auto rounded-lg border border-gray-200">
                <table className="ai-tracker-table w-full border-collapse">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-gray-100">
                      <th className="sticky left-0 top-0 z-20 w-[30%] min-w-[180px] border border-gray-200 bg-gray-100 px-3 py-1 text-left text-sm font-semibold text-gray-700 whitespace-nowrap">
                        <div className="flex items-center justify-between gap-2">
                          <span>
                            Math Concept
                            <span className="ml-1 text-xs font-normal text-gray-500">
                              ({tableData.length}개)
                            </span>
                          </span>

                          <button
                            ref={typeFilterButtonRef}
                            onClick={openTypeFilter}
                            className={`flex items-center gap-1 rounded-3xl px-2 py-1 text-xs transition-colors ${
                              totalSelectedFilters > 0
                                ? 'bg-primary/20 text-primary'
                                : 'bg-white text-gray-600 hover:bg-gray-200'
                            }`}
                          >
                            <span>Type</span>
                            {totalSelectedFilters > 0 && (
                              <span className="rounded-full bg-primary px-1.5 text-white">
                                {totalSelectedFilters}
                              </span>
                            )}
                            <ChevronDown className={`h-3 w-3 transition-transform ${showTypeFilter ? 'rotate-180' : ''}`} />
                          </button>

                          {showTypeFilter && createPortal(
                            <>
                              <div
                                className="fixed inset-0"
                                style={{ zIndex: 9999990 }}
                                onClick={() => setShowTypeFilter(false)}
                              />
                              <div
                                className="fixed rounded-lg border border-gray-200 bg-white py-2 shadow-lg"
                                style={{
                                  top: `${dropdownPosition.top}px`,
                                  left: `${dropdownPosition.left}px`,
                                  width: '320px',
                                  zIndex: 9999991,
                                }}
                              >
                                <div className="flex">
                                  <div className="w-1/2 border-r border-gray-100 px-2">
                                    <div className="mb-1 flex items-center justify-between px-1">
                                      <span className="text-[10px] font-semibold text-gray-500">Math Type</span>
                                      <button
                                        onClick={toggleAllMathTypes}
                                        className="text-[9px] text-primary hover:underline"
                                      >
                                        {selectedMathTypes.length === mathTypeList.length ? '해제' : '전체'}
                                      </button>
                                    </div>
                                    {mathTypeList.map(type => (
                                      <button
                                        key={type.code}
                                        onClick={() => toggleMathTypeFilter(type.code)}
                                        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-gray-50"
                                      >
                                        <span
                                          className="inline-flex h-4 w-8 items-center justify-center rounded text-[9px] font-semibold"
                                          style={{ backgroundColor: getMathTypeColor(type.code) }}
                                        >
                                          {type.code}
                                        </span>
                                        <span className="flex-1 text-left">{type.name}</span>
                                        {selectedMathTypes.includes(type.code) && (
                                          <Check className="h-3 w-3 text-primary" />
                                        )}
                                      </button>
                                    ))}
                                  </div>

                                  <div className="w-1/2 px-2">
                                    <div className="mb-1 flex items-center justify-between px-1">
                                      <span className="text-[10px] font-semibold text-gray-500">Concept Type</span>
                                      <button
                                        onClick={toggleAllConceptTypes}
                                        className="text-[9px] text-primary hover:underline"
                                      >
                                        {selectedTypes.length === conceptTypes.length ? '해제' : '전체'}
                                      </button>
                                    </div>
                                    {conceptTypes.map(type => (
                                      <button
                                        key={type.code}
                                        onClick={() => toggleConceptTypeFilter(type.code)}
                                        className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-xs hover:bg-gray-50"
                                      >
                                        <span
                                          className="inline-flex h-4 w-8 items-center justify-center rounded text-[9px] font-semibold"
                                          style={{ backgroundColor: getConceptTypeColor(type.code) }}
                                        >
                                          {type.code}
                                        </span>
                                        <span className="flex-1 text-left">{type.name}</span>
                                        {selectedTypes.includes(type.code) && (
                                          <Check className="h-3 w-3 text-primary" />
                                        )}
                                      </button>
                                    ))}
                                  </div>
                                </div>

                                {totalSelectedFilters > 0 && (
                                  <>
                                    <div className="mx-2 my-2 border-t border-gray-100" />
                                    <button
                                      onClick={resetAllFilters}
                                      className="w-full px-3 py-1.5 text-xs text-red-500 hover:bg-red-50"
                                    >
                                      필터 초기화
                                    </button>
                                  </>
                                )}
                              </div>
                            </>,
                            document.body
                          )}
                        </div>
                      </th>
                      <th className="sticky top-0 min-w-[120px] border border-gray-200 bg-gray-100 px-3 py-1 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">
                        <div className="flex flex-col items-center gap-0 leading-tight">
                          <span>Total</span>
                          {errorRates.total && errorRates.total.rate !== null && (
                            <>
                              <div className="flex items-center justify-center gap-3 text-[9px]">
                                <button
                                  onClick={() => handleSort('total', 'incorrect')}
                                  className="flex items-center gap-0.5 rounded px-1 py-0.5 transition-colors hover:bg-gray-200"
                                  title="오답수 기준 정렬"
                                >
                                  <span className="font-bold text-red-500">{errorRates.total.incorrect}</span>
                                  {getSortIcon('total', 'incorrect') === 'asc' && <ArrowUp className="h-2.5 w-2.5 text-primary" />}
                                  {getSortIcon('total', 'incorrect') === 'desc' && <ArrowDown className="h-2.5 w-2.5 text-primary" />}
                                  {!getSortIcon('total', 'incorrect') && <ArrowUpDown className="h-2.5 w-2.5 text-gray-300" />}
                                </button>
                                <span className="text-gray-400">/</span>
                                <button
                                  onClick={() => handleSort('total', 'count')}
                                  className="flex items-center gap-0.5 rounded px-1 py-0.5 transition-colors hover:bg-gray-200"
                                  title="전체문제수 기준 정렬"
                                >
                                  <span className="font-bold text-gray-900">{errorRates.total.count}</span>
                                  {getSortIcon('total', 'count') === 'asc' && <ArrowUp className="h-2.5 w-2.5 text-primary" />}
                                  {getSortIcon('total', 'count') === 'desc' && <ArrowDown className="h-2.5 w-2.5 text-primary" />}
                                  {!getSortIcon('total', 'count') && <ArrowUpDown className="h-2.5 w-2.5 text-gray-300" />}
                                </button>
                              </div>
                              <button
                                onClick={() => handleSort('total', 'rate')}
                                className={`flex items-center gap-0.5 rounded px-1 py-0.5 text-[9px] font-normal transition-colors hover:bg-gray-200 ${getErrorRateColor(errorRates.total.rate)}`}
                                title="오답률 기준 정렬"
                              >
                                (<span className="font-bold">{errorRates.total.rate}%</span>)
                                {getSortIcon('total', 'rate') === 'asc' && <ArrowUp className="h-2.5 w-2.5 text-primary" />}
                                {getSortIcon('total', 'rate') === 'desc' && <ArrowDown className="h-2.5 w-2.5 text-primary" />}
                                {!getSortIcon('total', 'rate') && <ArrowUpDown className="h-2.5 w-2.5 text-gray-300" />}
                              </button>
                            </>
                          )}
                        </div>
                      </th>
                      {['Lv1', 'Lv2', 'Lv3', 'Lv4', 'Lv5'].map((lv, idx) => {
                        const columnKey = `d${idx + 1}`;
                        const rate = errorRates[columnKey];
                        return (
                          <th key={lv} className="sticky top-0 min-w-[50px] border border-gray-200 bg-gray-100 px-1 py-1 text-center text-sm font-semibold text-gray-700 whitespace-nowrap">
                            <div className="flex flex-col items-center gap-0 leading-tight">
                              <span>{lv}</span>
                              {rate && rate.rate !== null && (
                                <>
                                  <div className="flex items-center justify-center gap-2 text-[9px]">
                                    <button
                                      onClick={() => handleSort(columnKey, 'incorrect')}
                                      className="flex items-center gap-0.5 rounded px-0.5 py-0.5 transition-colors hover:bg-gray-200"
                                      title="오답수 기준 정렬"
                                    >
                                      <span className="font-bold text-red-500">{rate.incorrect}</span>
                                      {getSortIcon(columnKey, 'incorrect') === 'asc' && <ArrowUp className="h-2 w-2 text-primary" />}
                                      {getSortIcon(columnKey, 'incorrect') === 'desc' && <ArrowDown className="h-2 w-2 text-primary" />}
                                      {!getSortIcon(columnKey, 'incorrect') && <ArrowUpDown className="h-2 w-2 text-gray-300" />}
                                    </button>
                                    <span className="text-gray-400">/</span>
                                    <button
                                      onClick={() => handleSort(columnKey, 'count')}
                                      className="flex items-center gap-0.5 rounded px-0.5 py-0.5 transition-colors hover:bg-gray-200"
                                      title="전체문제수 기준 정렬"
                                    >
                                      <span className="font-bold text-gray-900">{rate.count}</span>
                                      {getSortIcon(columnKey, 'count') === 'asc' && <ArrowUp className="h-2 w-2 text-primary" />}
                                      {getSortIcon(columnKey, 'count') === 'desc' && <ArrowDown className="h-2 w-2 text-primary" />}
                                      {!getSortIcon(columnKey, 'count') && <ArrowUpDown className="h-2 w-2 text-gray-300" />}
                                    </button>
                                  </div>
                                  <button
                                    onClick={() => handleSort(columnKey, 'rate')}
                                    className={`flex items-center gap-0.5 rounded px-0.5 py-0.5 text-[9px] font-normal transition-colors hover:bg-gray-200 ${getErrorRateColor(rate.rate)}`}
                                    title="오답률 기준 정렬"
                                  >
                                    (<span className="font-bold">{rate.rate}%</span>)
                                    {getSortIcon(columnKey, 'rate') === 'asc' && <ArrowUp className="h-2 w-2 text-primary" />}
                                    {getSortIcon(columnKey, 'rate') === 'desc' && <ArrowDown className="h-2 w-2 text-primary" />}
                                    {!getSortIcon(columnKey, 'rate') && <ArrowUpDown className="h-2 w-2 text-gray-300" />}
                                  </button>
                                </>
                              )}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {tableData.map((row) => {
                      const conceptType = row.conceptType ||
                        (row.conceptName && row.conceptName.includes(':') ?
                          row.conceptName.split(':')[0].split('-')[1] : null);

                      const displayName = row.conceptName.includes(':') ?
                        row.conceptName.split(':').slice(1).join(':').trim() :
                        row.conceptName;

                      return (
                        <tr key={row.conceptId} className="hover:bg-gray-50/50">
                          <td className="sticky left-0 z-10 w-[30%] min-w-[180px] border border-gray-200 bg-white px-1 py-0.5 text-sm">
                            <div className="flex items-center gap-2">
                              <div className="flex shrink-0 flex-col gap-0.5">
                                <span
                                  className="inline-flex items-center justify-center rounded font-semibold text-gray-700 hover:ring-2 hover:ring-primary/50"
                                  style={{
                                    backgroundColor: 'rgba(156, 163, 175, 0.3)',
                                    width: '28px',
                                    height: '16px',
                                    fontSize: '9px',
                                    cursor: 'pointer'
                                  }}
                                  title={`개념번호: ${row.conceptOrder} - 클릭하여 상세 정보 보기`}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedConceptId(row.conceptId);
                                    setShowMathceptModal(true);
                                  }}
                                >
                                  {row.conceptOrder}
                                </span>

                                {row.mathType && (
                                  <span
                                    className="inline-flex items-center justify-center rounded font-semibold text-gray-800"
                                    style={{
                                      backgroundColor: getMathTypeColor(row.mathType),
                                      width: '28px',
                                      height: '16px',
                                      fontSize: '8px'
                                    }}
                                    title={`Math Type: ${row.mathType}`}
                                  >
                                    {row.mathType}
                                  </span>
                                )}

                                {conceptType && (
                                  <span
                                    className="inline-flex items-center justify-center rounded font-semibold text-gray-800"
                                    style={{
                                      backgroundColor: getConceptTypeColor(conceptType),
                                      width: '28px',
                                      height: '16px',
                                      fontSize: '8px'
                                    }}
                                    title={getConceptTypeName(conceptType)}
                                  >
                                    {conceptType}
                                  </span>
                                )}
                              </div>

                              <span
                                className="min-w-0 flex-1 font-medium text-gray-800 line-clamp-3"
                                dangerouslySetInnerHTML={{ __html: renderedConceptNames.get(row.conceptId) || displayName }}
                              />

                              {/* 스냅샷 번호 표시 */}
                              {snapshots[row.conceptId] && snapshots[row.conceptId].length > 0 && (
                                <div className="flex shrink-0 gap-0.5">
                                  {snapshots[row.conceptId].map((num, idx) => (
                                    <span
                                      key={idx}
                                      className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-[9px] font-bold ${RAINBOW_COLORS[idx % 7]}`}
                                      title={`스냅샷 ${idx + 1}: ${num}번째`}
                                    >
                                      {num}
                                    </span>
                                  ))}
                                </div>
                              )}
                            </div>
                          </td>

                          <td className="border border-gray-200 px-1 py-0.5">
                            <HorizontalBar correct={row.total[0]} incorrect={row.total[1]} maxTotal={maxTotal} />
                          </td>

                          <td className="border border-gray-200 p-0 align-top">
                            <VerticalBar correct={row.difficulty1[0]} incorrect={row.difficulty1[1]} maxDifficultyTotal={maxDifficultyTotal} />
                          </td>
                          <td className="border border-gray-200 p-0 align-top">
                            <VerticalBar correct={row.difficulty2[0]} incorrect={row.difficulty2[1]} maxDifficultyTotal={maxDifficultyTotal} />
                          </td>
                          <td className="border border-gray-200 p-0 align-top">
                            <VerticalBar correct={row.difficulty3[0]} incorrect={row.difficulty3[1]} maxDifficultyTotal={maxDifficultyTotal} />
                          </td>
                          <td className="border border-gray-200 p-0 align-top">
                            <VerticalBar correct={row.difficulty4[0]} incorrect={row.difficulty4[1]} maxDifficultyTotal={maxDifficultyTotal} />
                          </td>
                          <td className="border border-gray-200 p-0 align-top">
                            <VerticalBar correct={row.difficulty5[0]} incorrect={row.difficulty5[1]} maxDifficultyTotal={maxDifficultyTotal} />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>

                {tableData.length === 0 && (
                  <div className="py-12 text-center text-gray-500">
                    해당 학생의 개념별 데이터가 없습니다.
                  </div>
                )}
              </div>
            </>
          )}

          {currentTab === 'engine' && (
            <div className="mt-2 min-h-0 flex-1 overflow-y-auto overflow-x-auto rounded-lg border border-gray-200">
              {engineError && (
                <div className="m-3 rounded bg-red-50 p-3 text-sm text-red-700">
                  {engineError}
                </div>
              )}
              {(isLoadingEngine || !engineData) ? (
                <div className="flex items-center justify-center py-10 text-gray-500">
                  <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
                </div>
              ) : engineData ? (
                <div className="min-h-0">
                  <div className="flex items-center gap-4 overflow-x-auto border border-gray-200 bg-gray-50 px-2 py-1 text-xs whitespace-nowrap text-gray-700">
                    <span><span className="font-semibold">exam</span>: {engineData.exam_counts}</span>
                    <span><span className="font-semibold">solved</span>: {engineData.solved_problems}</span>
                    <span><span className="font-semibold">unique</span>: {engineData.total_problems}</span>
                    <span><span className="font-semibold">unlabeled</span>: {engineData.unlabeled_problems}</span>
                    {engineData.delta?.exam?.exam_seq && (
                      <span><span className="font-semibold">delta</span>: last_exam #{engineData.delta.exam.exam_seq}</span>
                    )}
                    <span className="text-gray-500">
                      * 정렬: {engineTableSort.column ? (
                        <>
                          <span className="font-medium text-gray-700">
                            {engineTableSort.column === 'cid' ? 'Concept' :
                             engineTableSort.column === 'st' ? 'stability' :
                             engineTableSort.column === 'dr' ? 'Δr' :
                             engineTableSort.column === 'dh' ? 'Δh' :
                             engineTableSort.column === 'ds' ? 'Δst' :
                             engineTableSort.column === 'lastRecord' ? 'last_record' :
                             engineTableSort.column === 'totalRecord' ? 'total_record' :
                             engineTableSort.column === 'avgDiff' ? 'avg_diff' :
                             engineTableSort.column}
                          </span>
                          {' '}({engineTableSort.direction === 'desc' ? '내림차순' : '오름차순'})
                        </>
                      ) : '기본 (concept_index)'}
                    </span>
                  </div>

                  <table className="ai-tracker-table w-full border-collapse">
                    <thead className="sticky top-0 z-10">
                      <tr className="bg-gray-100">
                        <th
                          className="sticky left-0 z-20 min-w-[220px] border border-gray-200 bg-gray-100 px-2 py-1 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none"
                          onClick={() => handleEngineTableSort('cid')}
                        >
                          <span className="flex items-center gap-1">
                            Concept (concept_index)
                            <span className="text-[10px] text-gray-400">
                              {engineTableSort.column === 'cid' ? (engineTableSort.direction === 'desc' ? '▼' : '▲') : '↕︎'}
                            </span>
                          </span>
                        </th>
                        <th
                          className="min-w-[90px] border border-gray-200 px-2 py-1 text-right text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none"
                          onClick={() => handleEngineTableSort('r')}
                        >
                          <span className="flex items-center justify-end gap-1">
                            r
                            <span className="text-[10px] text-gray-400">
                              {engineTableSort.column === 'r' ? (engineTableSort.direction === 'desc' ? '▼' : '▲') : '↕︎'}
                            </span>
                          </span>
                        </th>
                        <th
                          className="min-w-[90px] border border-gray-200 px-2 py-1 text-right text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none"
                          onClick={() => handleEngineTableSort('h')}
                        >
                          <span className="flex items-center justify-end gap-1">
                            h
                            <span className="text-[10px] text-gray-400">
                              {engineTableSort.column === 'h' ? (engineTableSort.direction === 'desc' ? '▼' : '▲') : '↕︎'}
                            </span>
                          </span>
                        </th>
                        <th
                          className="min-w-[90px] border border-gray-200 px-2 py-1 text-right text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none"
                          onClick={() => handleEngineTableSort('st')}
                        >
                          <span className="flex items-center justify-end gap-1">
                            stability
                            <span className="text-[10px] text-gray-400">
                              {engineTableSort.column === 'st' ? (engineTableSort.direction === 'desc' ? '▼' : '▲') : '↕︎'}
                            </span>
                          </span>
                        </th>
                        <th
                          className="min-w-[90px] border border-gray-200 px-2 py-1 text-right text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none"
                          onClick={() => handleEngineTableSort('dr')}
                        >
                          <span className="flex items-center justify-end gap-1">
                            Δr
                            <span className="text-[10px] text-gray-400">
                              {engineTableSort.column === 'dr' ? (engineTableSort.direction === 'desc' ? '▼' : '▲') : '↕︎'}
                            </span>
                          </span>
                        </th>
                        <th
                          className="min-w-[90px] border border-gray-200 px-2 py-1 text-right text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none"
                          onClick={() => handleEngineTableSort('dh')}
                        >
                          <span className="flex items-center justify-end gap-1">
                            Δh
                            <span className="text-[10px] text-gray-400">
                              {engineTableSort.column === 'dh' ? (engineTableSort.direction === 'desc' ? '▼' : '▲') : '↕︎'}
                            </span>
                          </span>
                        </th>
                        <th
                          className="min-w-[90px] border border-gray-200 px-2 py-1 text-right text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none"
                          onClick={() => handleEngineTableSort('ds')}
                        >
                          <span className="flex items-center justify-end gap-1">
                            Δst
                            <span className="text-[10px] text-gray-400">
                              {engineTableSort.column === 'ds' ? (engineTableSort.direction === 'desc' ? '▼' : '▲') : '↕︎'}
                            </span>
                          </span>
                        </th>
                        <th
                          className="min-w-[110px] border border-gray-200 px-2 py-1 text-center text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none"
                          onClick={() => handleEngineTableSort('lastRecord')}
                        >
                          <span className="flex items-center justify-center gap-1">
                            last_record
                            <span className="text-[10px] text-gray-400">
                              {engineTableSort.column === 'lastRecord' ? (engineTableSort.direction === 'desc' ? '▼' : '▲') : '↕︎'}
                            </span>
                          </span>
                        </th>
                        <th
                          className="min-w-[110px] border border-gray-200 px-2 py-1 text-center text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none"
                          onClick={() => handleEngineTableSort('totalRecord')}
                        >
                          <span className="flex items-center justify-center gap-1">
                            total_record
                            <span className="text-[10px] text-gray-400">
                              {engineTableSort.column === 'totalRecord' ? (engineTableSort.direction === 'desc' ? '▼' : '▲') : '↕︎'}
                            </span>
                          </span>
                        </th>
                        <th
                          className="min-w-[120px] border border-gray-200 px-2 py-1 text-right text-xs font-semibold text-gray-700 cursor-pointer hover:bg-gray-200 select-none"
                          onClick={() => handleEngineTableSort('avgDiff')}
                        >
                          <span className="flex items-center justify-end gap-1">
                            avg_diff(n)
                            <span className="text-[10px] text-gray-400">
                              {engineTableSort.column === 'avgDiff' ? (engineTableSort.direction === 'desc' ? '▼' : '▲') : '↕︎'}
                            </span>
                          </span>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedEngineTableRows.map((row) => (
                        <tr key={row.cid} className="hover:bg-gray-50/50">
                          <td className="sticky left-0 z-10 border border-gray-200 bg-white px-2 py-1 text-sm">
                            <div className="flex items-start gap-2">
                              <span className="inline-flex h-5 min-w-[56px] items-center justify-center rounded bg-gray-100 px-2 text-[10px] font-semibold text-gray-700">
                                {row.order}
                              </span>
                              <span
                                className="min-w-0 flex-1 font-medium text-gray-800 line-clamp-2"
                                dangerouslySetInnerHTML={{ __html: row.nameRendered }}
                              />
                            </div>
                          </td>
                          <td className="border border-gray-200 px-2 py-1 text-right text-xs">{typeof row.r === 'number' ? row.r.toFixed(2) : (row.r ?? '-')}</td>
                          <td className="border border-gray-200 px-2 py-1 text-right text-xs">{typeof row.h === 'number' ? row.h.toFixed(2) : (row.h ?? '-')}</td>
                          <td className="border border-gray-200 px-2 py-1 text-right text-xs">{typeof row.st === 'number' ? row.st.toFixed(2) : (row.st ?? '-')}</td>
                          <td className="border border-gray-200 px-2 py-1 text-right text-xs">{typeof row.dr === 'number' ? row.dr.toFixed(2) : (row.dr ?? '-')}</td>
                          <td className="border border-gray-200 px-2 py-1 text-right text-xs">{typeof row.dh === 'number' ? row.dh.toFixed(2) : (row.dh ?? '-')}</td>
                          <td className="border border-gray-200 px-2 py-1 text-right text-xs">{typeof row.ds === 'number' ? row.ds.toFixed(2) : (row.ds ?? '-')}</td>
                          <td className="border border-gray-200 px-2 py-1 text-center text-xs">
                            {row.lastRecord ? `${row.lastRecord[0]}/${row.lastRecord[1]}` : '-'}
                          </td>
                          <td className="border border-gray-200 px-2 py-1 text-center text-xs">
                            {row.totalRecord ? `${row.totalRecord[0]}/${row.totalRecord[1]}` : '-'}
                          </td>
                          <td className="border border-gray-200 px-2 py-1 text-right text-xs">
                            {row.avgDiff === null ? '-' : `${row.avgDiff} (${row.avgDiffN})`}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="flex items-center justify-center py-10 text-gray-500">
                  Engine 데이터가 없습니다.
                </div>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="flex flex-1 items-center justify-center text-gray-500">
          {hasInitialLoaded
            ? <p>표시할 데이터가 없습니다.</p>
            : <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent"></div>
          }
        </div>
      )}

      {/* 매스컨셉 상세 정보 모달 */}
      <MathceptDetailModal
        isOpen={showMathceptModal}
        onClose={() => {
          setShowMathceptModal(false);
          setSelectedConceptId(null);
        }}
        conceptIds={selectedConceptId ? [selectedConceptId] : []}
        renderLatex={renderLatex}
        title="매스컨셉 상세 정보"
        useLoader={true}
        useBearerPrefix={true}
      />

      {/* 개념 추이(직접 개념 r 시계열) 모달 — 선택 개념 대상, 일별 재생 프레임 재사용 */}
      <ConceptTrendModal
        isOpen={showTrendModal}
        onClose={() => setShowTrendModal(false)}
        cids={chartSideSelectedCids}
        ensureFramesLoaded={ensureFramesLoaded}
        getConceptShortName={getConceptShortName}
        getConceptDisplayName={getConceptDisplayName}
        studentId={studentId}
        /* 이 부품은 세 화면이 공유한다 — 켜는 것은 호출부(학습 /students-hub)뿐이고
           기본값은 꺼짐이라 나머지 두 화면은 구형 미리보기 그대로다. */
        useV2Preview={useV2Preview}
        includeV2Assignments={includeV2Assignments}
      />

      <style>{`
        .ai-tracker-table tbody tr {
          content-visibility: auto;
          contain-intrinsic-size: auto 32px;
        }
      `}</style>
    </div>
  );
}

export default AITrackerContent;
